import React from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ThemeContext } from 'src/store/ContextStore';
import {
  config,
  applyCanvasPreferences,
  isPregnancyNode,
  isGameteDonorNode,
  isSurrogateNode,
  isReproSatelliteNode,
  pregnancyLossRenderLabel,
  isTerminationOfPregnancy,
  isStillbirth,
  isOngoingPregnancy,
  reproRoleLabelOf,
  donorRoleLabelOf,
  isReproNode
} from './pedigree/config';
import {
  childPath,
  spousePath,
  twinGroupPath,
  twinApex,
  reproDescentPath,
  bioConnectorPath
} from './pedigree/path';
import {
  nodeSymbolArea,
  nodeSymbolType,
  reproGlyphLetter,
  terminationStrikeEndpoints
} from './pedigree/reproSymbol';
import { deriveReproNodes } from './pedigree/deriveReproNodes';
import {
  probandOnlyActive,
  labelsForNode
} from 'src/components/pedigree-chart/labelScope';
import { initZoom } from './pedigree/zoom';
import {
  focusPointFor,
  shouldFrame,
  visibleCenter
} from './pedigree/openFrame';
import { usePedigreeInstance, makeIid } from './pedigree/instanceContext';
import { useAsyncLayout } from './useAsyncLayout';
import { captureEstablishedOrder } from './pedigree/layout';
import {
  stampUnionAnchors,
  unionRefreshPosition
} from './pedigree/unionAnchors';
import {
  computeReorderOutcome,
  mergeGenerationOrder
} from './pedigree/reorderForGeneration';
import { sibBarMidY } from './pedigree/sibGroupGeometry';
import { assignNomenclature } from './pedigree/nomenclature';
import {
  resolveLinkedRecordBadge,
  linkedRecordBadgeGeometry,
  linkedRecordGlyphTransform,
  linkedRecordBadgeLabel,
  LINK_GLYPH_PATH
} from './linked-records/linkedRecordBadge';
import { computeAutoSpacing } from './pedigree/autoSpacing';
import { GENDER } from './pedigree/config';
import {
  adoptedDescentDash,
  getAcronym,
  genderIconMap,
  getLabelPos,
  wrap,
  collectDiagnosesSource,
  selectDiagnosesRows,
  selectPhenotypeRows,
  selectGenotypeRows,
  selectExternalPatientIdRows,
  hasRowsToDraw,
  reproLabelOn
} from './pedigree/utils';
import {
  carrierStatusMarkText,
  carrierStatusMarkGeometry,
  topRightLaneX,
  PROXY_LABEL_FONT_SCALE
} from './pedigree/carrierStatusMark';
import {
  blockEnabled,
  blockLines,
  buildGenotypeRuns,
  LABEL_ROW_PITCH,
  STACKED_LABEL_BLOCKS
} from './pedigree/labelText';
import { overflowRowText } from './pedigree/labelValueFilter';
import {
  createPathWithHooksAtSegments,
  decomposePathIntoSegments,
  detectAllSegmentIntersections
} from './pedigree/lineIntersection';
import { geneMarker, MARKER_KIND } from 'src/util/variantPathogenicity';
import { CLINICAL_COLOURS } from 'src/theme/palette';

// The green used by the clinically-confirmed-diagnosis tick. Named so the
// classification-reviewed tick cannot drift away from it — they are the same
// signal to a reader and must stay the same colour. From the clinical register
// (src/theme/palette.js), which the legend reads too.
const CONFIRMED_TICK_COLOUR = CLINICAL_COLOURS.clinicallyConfirmed;
import {
  partialDateFromBackend,
  formatPartialDate
} from 'src/util/partialDate';
import { calculateAgePartial, formatAgeDisplay } from 'src/util/util';
import {
  getCircleSegmentArc,
  getSquareSegmentPolyline,
  getVerticalLineEnds
} from './carrier-status/segmentPerimeter';
import { isCarrierMark } from './carrier-status/constants';
import { buildDiagnoseSegments } from './carrier-status/diagnoseSegments';
import { getProbandState } from './pedigree/probandState';
import { rowUnitsFromY } from './pedigree/rowUnits';
import { colUnitsFromX } from './pedigree/colUnits';
import { noteWrapWidthFromNeed } from './pedigree/labelText';
import {
  adoptedBracketsPath,
  fertilityMarkPath,
  probandArrowLine,
  probandArrowRotation,
  PROBAND_ARROW_HEAD_PATH,
  PROBAND_ARROW_MARKER
} from './pedigree/nodeMarkPaths';
import { highlightAnalysisCards } from '../pedigree-sidebar/sections/clinicalInsightsUi';
import { paintDataOverlays, clearDataOverlays } from './data-overlays/paint';
import {
  CASCADE_BADGE_COLORS,
  getCascadeBadgeTier,
  cascadeBadgeText
} from './analysis/cascadeBadge';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Status messages shown under the loading spinner while the worker computes the layout.
// They advance in order (then hold on the last) to convey progress through the pipeline —
// loosely: parse → generation assignment → ordering → x-assignment/refine → render. Not a
// precise progress bar; just reassurance that work is happening and worth the wait.
const LOADING_PHRASES = [
  'Loading family',
  'Building generations',
  'Arranging relationships',
  'Optimizing layout',
  'Drawing the pedigree'
];

// Two Map<uid, rank> orders are equal iff same keys and same rank for each. Used to avoid
// re-persisting / re-capturing an establishedOrder that hasn't actually changed.
const ordersEqual = (a, b) => {
  if (!a || !b || a.size !== b.size) return false;
  for (const [k, v] of a) if (b.get(k) !== v) return false;
  return true;
};

export default function Canvas({
  options,
  pedRef,
  handleClick,
  onNodeClick,
  onEdgeClick,
  // Double-click on a derived pregnancy/fetus node → open that record's editor (REQ-H4).
  onReproNodeDoubleClick,
  onCloseMenu,
  members,
  proband,
  colors,
  labelsSet,
  carrier = {},
  // TGCLOUD-1793 (1504 B4). Canonical disease id -> { color, name }, taken from
  // useCarrierStatus's ALWAYS-computed projection. Deliberately a SEPARATE prop
  // from `carrier`: Canvas is handed `displayCarrier`, which is EMPTY_CARRIER
  // whenever FEATURE_CARRIER_STATUS_IN_CONDITION is off — the shipped default —
  // so `carrier.diseaseColorMap` is `{}` here and cannot drive the fill.
  diseaseColorMap = {},
  carrierStyle = {},
  analysis = null,
  customPositions,
  setCustomPositions,
  establishedOrder = null,
  setEstablishedOrder = null,
  orderLocked = false,
  setOrderLocked = null,
  onArrangeHint = null,
  onReorderRefused = null,
  // TGCLOUD-1909: told when a relayout after the first is in flight and when it
  // lands, so the chart box can show the arranging chip beside its editing
  // banner (the two must centre on the same working area).
  onArrangingChange = null,
  freehandModeRef = null,
  spacePanRef = null,
  selectedIdsRef = null,
  lockedPersonsRef = null,
  lockedPersonsVersion = 0,
  dataOverlaySlotsRef = null,
  dataOverlayStyleRef = null,
  dataOverlayVersion = 0,
  onLassoSelect = null,
  lassoEnabledRef = null,
  canvasActionsRef = null,
  linkStatusMap = {},
  onBadgeClick = null,
  reanchorPidRef = null,
  // TGCLOUD-1863: the person the clinician arrived for (a deep link, a Data
  // Explorer row, a person tab); the open frames on them instead of the proband.
  focusPidRef = null,
  // TGCLOUD-1863: width of the detail drawer floating over the right of the
  // canvas, so the open frames the focus person in the UNCOVERED area.
  viewInsetRef = null,
  skipPinRelayoutRef = null,
  orientationSeedRef = null,
  onHistoryCheckpoint = null,
  onAutomaticLayoutWrite = null
}) {
  const t = useAutoTranslation();

  let height = 100;
  let width = 100;

  // Brand/header colour (the teal the rest of the chrome uses) for the loading spinner.
  const { theme } = useContext(ThemeContext);

  // Per-instance DOM-id namespacer. With one chart mounted (`instanceId` null) it
  // returns ids unchanged, so the classic single-chart SVG is byte-identical; with
  // several charts open at once each gets a unique suffix so their `url(#…)`
  // functional IRIs, clip paths and lookup ids never collide. Applied to BOTH id
  // creation and every url(#…) reference in the render effect below.
  const { instanceId, isActive, containerRef } = usePedigreeInstance();
  const iid = makeIid(instanceId);

  // Tri-state proband read (see pedigree/probandState.js): 'explicit' promoted member → arrow on that
  // member; 'cleared' → probandPid null, NO arrow ("no active proband" is a real state); 'legacy'
  // (un-migrated data) → structural-root fallback keeps the arrow where it always was.
  //
  // Hoisted to the top of the component because BOTH the render effect and the auto-spacing calls below
  // need it: proband-only label control (TGCLOUD-1465) changes which labels reserve a row, and spacing is
  // derived from those rows, so the two must be computed from the same proband.
  const probandState = React.useMemo(() => getProbandState(members), [members]);

  const isChartProband = React.useCallback(
    (person) =>
      probandState.probandPid != null &&
      String(person?.pid) === String(probandState.probandPid),
    [probandState]
  );

  // Options for LABEL GEOMETRY: identical to `options` plus the resolved proband, which
  // computeLabelLayout needs to make the same per-node suppression decision the renderer makes.
  const labelGeomOptions = React.useMemo(
    () => ({ ...options, probandPid: probandState.probandPid }),
    [options, probandState.probandPid]
  );

  // Flips true once the Inter web font has loaded; re-runs the layout effect so
  // auto-spacing re-measures label widths with the real font (see effect below).
  const [fontsReady, setFontsReady] = useState(false);

  // True until the first worker layout of this mount lands: the reopen layout
  // locks to the saved order (exact reproduction); later live layouts in
  // as-drawn mode re-optimise with the order as an orientation seed only.
  const firstOpenLockRef = useRef(true);

  // Layout result computed OFF the main thread (Web Worker), fed to the render
  // effect below. Holds {nodes, edges, nodesById} plus the spacing the layout used
  // (so render-time config reads match the geometry). null until the first result.
  const [layout, setLayout] = useState(null);
  // Label geometry for the RENDER: the options above plus the per-node needs
  // the settled layout was spaced with (TGCLOUD-1908), so a Person Note wraps
  // and reserves rows at its own node's width. The layout effect keeps using
  // `labelGeomOptions` (it measures BEFORE a layout exists).
  const renderLabelGeomOptions = React.useMemo(
    () => ({ ...labelGeomOptions, colNeeds: layout?.colNeeds ?? null }),
    [labelGeomOptions, layout]
  );
  const layoutReqRef = useRef(0);

  // Drop the layout when the FAMILY changes (TGCLOUD-1804).
  //
  // `layout` is only ever replaced by a SUCCESSFUL worker result whose reqId is
  // still current, and that is deliberate — clearing it on every input change
  // would tear the SVG down mid-interaction during an in-flight relayout, which
  // is a real reported defect. But it also meant a family whose layout failed,
  // or was cancelled, or had not landed yet kept painting the last family that
  // succeeded IN THIS MOUNT: correct header, someone else's chart.
  //
  // Scoped to the family rather than to the inputs so the anti-tearing
  // behaviour is untouched: within one family every relayout still holds the
  // previous picture until the new one is ready. Belt-and-braces alongside the
  // key on <Pedigree> in PedigreeBox — that already remounts on a family
  // change, and this makes the Canvas correct on its own terms rather than by
  // relying on a parent's key.
  const familyKey = proband?._id ?? proband?.familyId ?? null;
  const lastFamilyKeyRef = useRef(familyKey);
  if (lastFamilyKeyRef.current !== familyKey) {
    lastFamilyKeyRef.current = familyKey;
    // During render, not in an effect: an effect would let one paint through
    // with the previous family's nodes still on screen.
    if (layout !== null) setLayout(null);
  }
  // Hint-only regrade requests after a pin drop (see the pin-skip branch) —
  // separate counter so they never disturb the real request ordering.
  const hintReqRef = useRef(0);
  const runLayout = useAsyncLayout();

  // Multi-pedigree-tabs: the layout `config` (SYMBOL_SIZE/NODE_GAP/LEVEL_HEIGHT)
  // is a module singleton set at render time from THIS chart's layout. With
  // several charts mounted and different custom spacing, another chart's render
  // could leave the singleton on its values; interactions that read config later
  // (drag snap, union refresh) would then use the wrong geometry. Re-assert this
  // chart's spacing whenever it becomes the active tab. Idempotent for a lone
  // chart (isActive always true, same values the render already set).
  useEffect(() => {
    if (isActive && layout) {
      config.NODE_GAP = layout.nodeGap;
      config.LEVEL_HEIGHT = layout.levelHeight;
      config.SYMBOL_SIZE = layout.symbolSize;
      config.AUTO_ROW_NEEDS = layout.rowNeeds ?? null;
      config.AUTO_COL_NEEDS = layout.colNeeds ?? null;
    }
  }, [isActive, layout]);
  // Set when WE update establishedOrder by capturing the settled layout's own order
  // (first-open capture / incremental capture). That order reproduces the current
  // geometry, so the establishedOrder-triggered re-run of this effect would just
  // rebuild the identical SVG — a visible flash. This flag makes that one re-run a
  // no-op. (Phase B2 / reused by Phase C4.)
  const skipCaptureRelayoutRef = useRef(false);

  // Cycle the spinner's status text while the (worker) layout is pending. Advances through
  // LOADING_PHRASES and holds on the last; resets on each fresh load. No-op once `layout` is set.
  const [loadingStep, setLoadingStep] = useState(0);
  // TGCLOUD-1909: a relayout after the first (a reorder, an add, Auto-arrange) can
  // take seconds on a large family; the chart box says so until the result lands.
  const arrangingRef = useRef(false);
  const setArranging = (on) => {
    if (arrangingRef.current === on) return;
    arrangingRef.current = on;
    if (onArrangingChange) onArrangingChange(on);
  };
  useEffect(() => {
    if (layout) return undefined;
    setLoadingStep(0);
    const id = setInterval(() => {
      setLoadingStep((s) => Math.min(s + 1, LOADING_PHRASES.length - 1));
    }, 1600);
    return () => clearInterval(id);
  }, [layout]);

  // Only these option changes affect LAYOUT: the canvas preferences (spacing,
  // symbol size, ...) always, and the labels set ONLY while auto-spacing measures
  // label widths. A pure label toggle otherwise repaints via the render effect
  // WITHOUT re-running the worker - which is a multi-second stall per checkbox
  // on a large family.
  const layoutPrefsKey = React.useMemo(() => {
    if (!options) return 'none';
    const { labelsSet, ...prefs } = options;
    return JSON.stringify({
      prefs,
      labelsSet: options.auto_spacing ? labelsSet : null
    });
  }, [options]);

  // Compute the layout asynchronously whenever its inputs change. A large /
  // deeply-interconnected family can take tens of seconds to minutes to lay out
  // (best-of-both + bidirectional reorder search); running it in a worker keeps the
  // UI responsive instead of freezing the tab. Auto-spacing measurement stays on
  // the main thread (it may touch the DOM); its NODE_GAP/LEVEL_HEIGHT is passed to
  // the worker and stashed on the result so render reproduces the same spacing.
  useEffect(() => {
    // B2: skip the re-run caused by our own establishedOrder capture — it reproduces
    // the current geometry, so re-laying it out would flash the chart for nothing.
    if (skipCaptureRelayoutRef.current) {
      skipCaptureRelayoutRef.current = false;
      return;
    }
    // TGCLOUD-1457: skip the re-run caused by a PIN-ONLY change (a move-mode / group-drag
    // drop writing customPositions). The drag already moved the node and its edges live, so
    // the layout inputs are unchanged — re-running would tear the SVG down mid-interaction
    // (grabbing the next node during the in-flight worker relayout was Kathryn's "this one
    // moved") for zero visual gain. The pin is still persisted (PedigreeChart's autosave)
    // and applied by the worker on the next REAL relayout.
    //
    // 2026-08-12 (Tim's ruling): the ARRANGE BEACON must still re-grade after a
    // pin drop — a deliberately de-optimising drag has to light the button. The
    // skip stands (nothing is applied, no SVG teardown); a background HINT-ONLY
    // layout is dispatched whose result updates only the beacon state. Guards:
    // only when the current layout is settled (in the single-slot worker
    // plumbing a hint request would supersede an in-flight real one) and not in
    // the parallel-pool mode (its cancelBelow ordering assumes numeric reqIds;
    // pin damage there still grades on the next real relayout via the engine's
    // pin-free reference).
    if (skipPinRelayoutRef?.current) {
      skipPinRelayoutRef.current = false;
      const settled = layout && layout.key === layoutReqRef.current;
      if (
        config.ENABLE_ARRANGE_BEACON &&
        onArrangeHint &&
        settled &&
        config.ENABLE_PARALLEL_LAYOUT !== true &&
        proband
      ) {
        const realAtDispatch = layoutReqRef.current;
        const myHint = ++hintReqRef.current;
        const haveOrderNow =
          config.ENABLE_ESTABLISHED_ORDER &&
          establishedOrder &&
          establishedOrder.size > 0;
        let hintSpacing = null;
        if (options.auto_spacing) {
          const auto = computeAutoSpacing(members, labelGeomOptions);
          hintSpacing = {
            nodeGapPx: auto.nodeGapPx,
            levelHeightPx: auto.levelHeightPx,
            // The hint layout must see the same per-generation geometry as the
            // real one, or the beacon grades a chart that is not on screen.
            rowNeeds: auto.rowNeeds || null,
            colNeeds: auto.colNeeds || null
          };
        }
        runLayout(
          {
            reqId: `hint-${myHint}`,
            family: deriveReproNodes(members),
            familyId: proband.familyId,
            probandId: proband._id,
            customPositions,
            establishedOrder: haveOrderNow ? establishedOrder : null,
            prefs: options,
            spacing: hintSpacing
          },
          (res) => {
            if (hintReqRef.current !== myHint) return; // a newer hint is pending
            if (layoutReqRef.current !== realAtDispatch) return; // a real relayout superseded us
            if (res.ok) onArrangeHint(!!res.arrangeHint);
          }
        );
      }
      return;
    }
    // TGCLOUD-1863: nothing to lay out until the family has arrived. The canvas
    // mounts under the family fetch with an empty member list; laying THAT out
    // rendered an empty chart, framed it with the legacy top-left fallback and
    // latched the first-open framing, so the real layout then arrived as a
    // re-render and restored a view with the proband off-screen — only when the
    // config fetch outlasted the 250 ms dispatch debounce, which is why it read
    // as a large-family problem. The spinner simply stays up instead.
    if (!members || members.length === 0) return;
    // B1: with auto-spacing, defer the FIRST layout until the web font is loaded so we
    // don't lay out once with fallback metrics and visibly reflow when the font arrives.
    // The spinner stays up until then (layout is still null).
    if (options.auto_spacing && !fontsReady) return;
    applyCanvasPreferences(options);
    let spacing = null;
    // Manual spacing is uniform by definition — clear any needs a previous
    // auto-spacing run left on the singleton (TGCLOUD-1610).
    config.AUTO_ROW_NEEDS = null;
    config.AUTO_COL_NEEDS = null;
    if (options.auto_spacing) {
      const auto = computeAutoSpacing(members, labelGeomOptions);
      config.NODE_GAP = auto.nodeGapPx;
      config.LEVEL_HEIGHT = auto.levelHeightPx;
      config.AUTO_ROW_NEEDS = auto.rowNeeds || null;
      // Per-node horizontal needs (TGCLOUD-1908): the worker solves the column
      // map from them and ships it back as `cols`.
      config.AUTO_COL_NEEDS = auto.colNeeds || null;
      spacing = {
        nodeGapPx: auto.nodeGapPx,
        levelHeightPx: auto.levelHeightPx,
        rowNeeds: auto.rowNeeds || null,
        colNeeds: auto.colNeeds || null
      };
    }
    const nodeGap = config.NODE_GAP;
    const levelHeight = config.LEVEL_HEIGHT;
    const symbolSize = config.SYMBOL_SIZE;
    const rowNeeds = config.AUTO_ROW_NEEDS;
    const colNeeds = config.AUTO_COL_NEEDS;

    // D2 one-shot upgrade: legacy pixel-only pins gain grid units under the
    // CURRENT spacing (by definition, exactly where they render today) so they
    // track future spacing/auto-spacing changes. Triggers one extra effect pass
    // (identical geometry), then never again; the autosave persists the upgrade.
    //
    // The gy divide stays scalar: this runs BEFORE the layout, so there are no
    // laid-out rows to resolve against, and the pixel being converted was saved
    // under some other historical spacing anyway — the row-unit value it lands
    // on is the same approximation this upgrade has always made. Once upgraded,
    // the pin resolves and re-writes through the row map (TGCLOUD-1610).
    if (customPositions?.size && setCustomPositions) {
      let hasLegacy = false;
      for (const v of customPositions.values()) {
        if (v && v.gx == null && v.x != null) {
          hasLegacy = true;
          break;
        }
      }
      if (hasLegacy) {
        // AUTOMATIC WRITER. Two consequences, both carried by this one call:
        // it must not create an undo step (resolve any armed checkpoint first,
        // see the settled-order capture below), and it must not be reported as
        // a user edit — a one-shot pin upgrade on open is not something the
        // clinician did, and treating it as one took the edit lock on open
        // (TGCLOUD-1665).
        if (onAutomaticLayoutWrite) onAutomaticLayoutWrite('positions');
        setCustomPositions((prev) => {
          const upgraded = new Map();
          for (const [k, v] of prev) {
            upgraded.set(
              k,
              v && v.gx == null && v.x != null
                ? { ...v, gx: v.x / nodeGap, gy: v.y / levelHeight }
                : v
            );
          }
          return upgraded;
        });
        return; // re-runs with upgraded pins; layout starts on that pass
      }
    }
    const reqId = ++layoutReqRef.current;
    setArranging(reqId > 1);
    // The persisted within-generation order is a hard LOCK when the user
    // drag-reordered (orderLocked), for the FIRST layout after opening — so a
    // reopen always reproduces exactly the arrangement that was saved (the
    // locked pipeline's byte-stable guarantee) — and, with the ARRANGE BEACON
    // on, for every live layout too: drawing never reshuffles placed people;
    // when the engine's from-scratch optimum is strictly better it flags
    // `arrangeHint` on the result and the Auto-arrange button lights up, so
    // the USER decides when to adopt the tidier arrangement. With the beacon
    // off ("as-drawn" mode, d9412899) live layouts re-optimise fully and may
    // reorder existing people.
    const haveOrder =
      config.ENABLE_ESTABLISHED_ORDER &&
      establishedOrder &&
      establishedOrder.size > 0;
    const orderForLayout =
      haveOrder &&
      (orderLocked || firstOpenLockRef.current || config.ENABLE_ARRANGE_BEACON)
        ? establishedOrder
        : null;
    // In as-drawn mode live layouts run PLAIN from-scratch (canonical, single
    // pipeline pass): displayed always equals canonical, so orientation is
    // stable by construction and no seed is needed. The engine's orientationSeed
    // remains available (one-shot ref) for future explicit uses.
    const orientationSeed =
      !orderForLayout && orientationSeedRef?.current?.size
        ? orientationSeedRef.current
        : null;
    if (orientationSeed) orientationSeedRef.current = null;
    runLayout(
      {
        reqId,
        // Derive reproductive render nodes (pregnancies + gamete-donor satellites) from
        // each carrier's pregnancy records BEFORE layout. deriveReproNodes returns a new
        // array (never mutating the persisted `members`) and is a no-op when the flag is
        // off or no member carries a record — so ordinary pedigrees are unaffected.
        family: deriveReproNodes(members),
        familyId: proband.familyId,
        probandId: proband._id,
        customPositions,
        establishedOrder: orderForLayout,
        orientationSeed,
        prefs: options,
        spacing
      },
      (res) => {
        if (res.reqId !== layoutReqRef.current) return; // a newer request supersedes this one
        setArranging(false);
        if (res.ok) {
          firstOpenLockRef.current = false;
          setLayout({
            nodes: res.nodes,
            edges: res.edges,
            nodesById: res.nodesById,
            key: res.reqId,
            nodeGap,
            levelHeight,
            symbolSize,
            // The laid-out row table + the needs that produced it: pin writes
            // resolve against `rows`, and the config singleton is re-asserted
            // from `rowNeeds` on tab switch / render (TGCLOUD-1610).
            rows: res.rows ?? null,
            rowNeeds,
            // The column map the layout was drawn through + the needs that
            // produced it: pin writes invert the map, and the config singleton
            // is re-asserted from the needs on tab switch / render (TGCLOUD-1908).
            cols: res.cols ?? null,
            colNeeds,
            simplifiedLayout: !!res.simplifiedLayout
          });
          // ARRANGE BEACON: report on EVERY settled layout — true lights the
          // Auto-arrange button, false clears a stale hint (e.g. right after an
          // Auto-arrange, when the displayed layout IS the optimum again).
          if (onArrangeHint) onArrangeHint(!!res.arrangeHint);
          // ESTABLISH + INCREMENTALLY MERGE the settled order (C4). Capture after EVERY
          // layout, not just the first: a locked layout keeps established people in their
          // order and slots newly-added people in (C1/C2), so recapturing folds those
          // newcomers into the lock — they, too, stay put on the next edit, and a reopen
          // reproduces the whole arrangement (not just the originally-captured core). After
          // an Auto-arrange clears the order, the next (unlocked) full optimise is captured
          // and re-locked — the "tidy at a settle point, then sit-and-forget" behaviour.
          // Only when it actually changed, and flash-free (skip its own relayout — B2).
          if (config.ENABLE_ESTABLISHED_ORDER && setEstablishedOrder) {
            const captured = captureEstablishedOrder(res.nodes);
            if (captured.size > 0 && !ordersEqual(captured, establishedOrder)) {
              // AUTOMATIC WRITER. Resolve any armed checkpoint BEFORE this write —
              // the capture's re-ranks (e.g. after a person add) must never be
              // misattributed to a preceding no-op gesture (which would commit a
              // phantom undo step and clear the redo stack) — and keep it out of
              // the edit signal. This one fires on the FIRST open of any family
              // whose stored order differs from the settled layout, i.e. once per
              // user per family, which is why the false lock looked intermittent.
              if (onAutomaticLayoutWrite) onAutomaticLayoutWrite('order');
              skipCaptureRelayoutRef.current = true;
              setEstablishedOrder(captured);
            }
          }
        }
        // res.ok === false: keep the previous layout rather than blanking the chart.
      }
    );
  }, [
    members,
    proband,
    customPositions,
    establishedOrder,
    setEstablishedOrder,
    orderLocked,
    layoutPrefsKey,
    fontsReady,
    runLayout
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const zoomRef = React.useRef(null);
  const svgRef = React.useRef(null);
  // Identity of the pedigree whose viewport we've already framed. On the FIRST
  // render of a pedigree we fit-to-content (so it never opens to blank space, and
  // no stale cross-pedigree zoom carries in); subsequent re-renders (edits) keep
  // the user's live pan/zoom.
  const viewInitRef = React.useRef(null);
  // TGCLOUD-1863 (pedigree/openFrame.js): whether a NON-EMPTY layout of this
  // pedigree has been framed, and whether the clinician has touched the view
  // since the open. The framing holds through the open-time relayouts until a
  // touch; the empty layout the canvas used to frame (and latch) never frames.
  const framedRef = React.useRef(false);
  const userTouchedRef = React.useRef(false);
  const markViewTouched = React.useCallback(() => {
    userTouchedRef.current = true;
  }, []);
  // Always-fresh handle on the current laid-out nodes for the d3 drag handlers (which are
  // bound inside the render effect and would otherwise close over a stale `nodes` array if
  // the effect re-ran mid/between drags). Updated wherever the render effect reads `layout`.
  const nodesRef = React.useRef(null);
  // Previous layout's node map + node count, so a re-render can tell a STRUCTURAL change
  // (a relative was added/removed → count changed) from a cosmetic one, and measure how far
  // the edited node moved. Used to keep that node fixed on screen (see the render effect's
  // reanchor block + zoom.js `reanchorWorldDelta`).
  const prevNodesByIdRef = React.useRef(null);
  const prevNodeCountRef = React.useRef(null);

  const labelOpts = labelsSet || options?.labelsSet || {};
  // PROBAND-ONLY LABEL CONTROL (TGCLOUD-1465). Suppresses the IDENTIFYING labels on everyone but the
  // proband, so a clinician can hand a physician a de-identified copy that still names the patient it is
  // about. Which labels count as identifying comes from PII_LABEL_KEYS — the same list full anonymisation
  // uses — so the two features cannot disagree.
  //
  // `probandOnly` is evaluated ONCE per chart (it also encodes the fail-open no-proband rule); the two
  // variants are memoised so the per-node choice below never allocates.
  const probandOnly = probandOnlyActive(labelOpts, probandState.probandPid);
  const piiSuppressedOpts = React.useMemo(
    () => labelsForNode(labelOpts, { isProband: false, active: true }),
    [labelOpts]
  );
  // The label set that applies to ONE node. Used by every filter that reads a PII key.
  const labelsFor = React.useCallback(
    (d) =>
      probandOnly && !isChartProband(d?.data || d)
        ? piiSuppressedOpts
        : labelOpts,
    [probandOnly, isChartProband, piiSuppressedOpts, labelOpts]
  );
  // Per-label bold/italic formatting. `labelOpts.formatting` is only populated
  // through the sidebar + scoped-preset resolution; when nothing is formatted it
  // is absent/empty and every applyFmt below writes NOTHING.
  const fmtMap = labelOpts.formatting || null;
  const fmtFor = (key) => (fmtMap && fmtMap[key]) || null;
  // d3 .call() helpers — set an attribute ONLY when truthy (never a null attr),
  // so an unformatted label is untouched.
  const applyFmtObj = (f) => (sel) => {
    if (!f) return sel;
    if (f.bold) sel.attr('font-weight', 'bold');
    if (f.italic) sel.attr('font-style', 'italic');
    return sel;
  };
  const applyFmt = (key) => applyFmtObj(fmtFor(key));
  // Render a composed label as per-field styled runs: one <tspan> per
  // { text, key, bold?, italic? } run, each taking its label key's formatting
  // (so every sub-field styles independently). `bold`/`italic` on a run force
  // the style regardless (e.g. the phenotype "Absent" suffix). The caller
  // re-centres via getBBox AFTER this, so the width reflects the styling.
  const appendRuns = (textSel, runs) => {
    (runs || []).forEach((run) => {
      if (run.text === '') return;
      const f = fmtFor(run.key);
      const ts = textSel.append('tspan').text(run.text);
      if (run.bold || (f && f.bold)) ts.attr('font-weight', 'bold');
      if (run.italic || (f && f.italic)) ts.attr('font-style', 'italic');
    });
  };
  // First + last name as independently-styleable runs (mirrors getName()).
  const nameRuns = (d) => {
    const first = d?.firstName || '';
    const last = d?.lastName || '';
    // Scoped per node: under proband-only mode a relative's name runs are empty.
    const ls = labelsFor(d);
    if (ls.firstName && ls.lastName)
      return [
        { text: first, key: 'firstName' },
        { text: ' ', key: null },
        { text: last, key: 'lastName' }
      ];
    if (ls.firstName) return [{ text: first, key: 'firstName' }];
    if (ls.lastName) return [{ text: last, key: 'lastName' }];
    return [];
  };

  const fmtDate = (v) => {
    if (!v) return '';
    const d = typeof v === 'string' ? new Date(v) : v;
    return isNaN(d) ? '' : d.toLocaleDateString();
  };

  // Precision-aware date formatter for pedigree node labels. Falls back to
  // the legacy fmtDate when only the ISO string is available (no precision
  // metadata).
  const fmtDateOnNode = (d, kind /* 'dob' | 'dod' */) => {
    const iso = kind === 'dob' ? d.dob : d.dod;
    if (!iso) return '';
    const partial = partialDateFromBackend({
      iso,
      estimatedFlag: kind === 'dob' ? d.estimatedDob : d.estimatedDod,
      precisionField: kind === 'dob' ? d.dobPrecision : d.dodPrecision
    });
    return partial ? formatPartialDate(partial) : fmtDate(iso);
  };
  const lineH = 16 * 5 * 2.35;

  useEffect(() => {
    // Layout is computed asynchronously in a worker (see the effect above). Nothing
    // to draw until the first result arrives — a spinner overlays meanwhile, and any
    // existing SVG is kept while a re-layout is in flight so the chart never blanks.
    if (!layout) return;
    // Reproduce the spacing the (worker-computed) layout actually used, so render-time
    // reads (label-wrap width = NODE_GAP - SYMBOL_SIZE, nomenclature gutter, symbol
    // size) match the baked geometry. applyCanvasPreferences sets the same SYMBOL_SIZE.
    config.NODE_GAP = layout.nodeGap;
    config.LEVEL_HEIGHT = layout.levelHeight;
    config.SYMBOL_SIZE = layout.symbolSize;
    config.AUTO_ROW_NEEDS = layout.rowNeeds ?? null;
    config.AUTO_COL_NEEDS = layout.colNeeds ?? null;
    // Each Person Note wraps at its own node's label-block width when the
    // layout was spaced per node (TGCLOUD-1908); else at the global gap.
    const noteWrapWidthFor = (d) => {
      const need =
        layout.colNeeds && d?.pid != null
          ? layout.colNeeds[String(d.pid)]
          : undefined;
      return need != null
        ? noteWrapWidthFromNeed(need, config.SYMBOL_SIZE)
        : config.NODE_GAP - config.SYMBOL_SIZE;
    };

    const svgContainer = d3.select(pedRef.current);

    // Remove old svg if exists
    svgContainer.select('svg').remove();

    // Append fresh svg
    const svg = svgContainer
      .append('svg')
      .attr('id', iid('pedigree-chart'))
      // Stable class the id used to carry: CSS (App.css) and export/lookup code
      // key on this class so the id can be namespaced per instance.
      .attr('class', 'pedigree-chart-svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .style('background', '#ffffffff');

    const svgNode = svg.node();
    width = svgNode.clientWidth;
    height = svgNode.clientHeight;

    // Lasso state shared across the SVG's mousedown/move/up handlers and the
    // canvas rect's click handler. lassoJustFinished suppresses the trailing
    // click that fires after a successful lasso drag, so that
    // background-click-to-deselect doesn't immediately wipe the selection.
    let lassoJustFinished = false;
    let activeLassoListeners = null;

    svg
      .append('rect')
      .attr('class', 'canvas')
      .attr('width', '100%')
      .attr('height', '100%')

      .on('contextmenu', function (e) {
        e.preventDefault();
        e.stopPropagation();
        onCloseMenu();
      })
      .on('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (lassoJustFinished) {
          lassoJustFinished = false;
          return;
        }
        onCloseMenu();
      });

    let gridSize = config.GRID_SIZE;
    const gridCell = gridSize * 2;
    // Snap to the center of a grid cell when the node crosses 20% into it
    const snapToGrid = (val) => {
      const lower = Math.floor(val / gridCell) * gridCell;
      const fraction = (val - lower) / gridCell;
      return fraction > 0.2 ? lower + gridCell : lower;
    };
    const dotR = options.enable_grid ? 5 : 0;
    const dotColor = '#8e87a4ff';
    const defs = svg.append('defs');
    const dotPattern = defs
      .append('pattern')
      .attr('class', 'pattern')
      .attr('id', iid('dot-grid'))
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', gridSize * 2)
      .attr('height', gridSize * 2);

    dotPattern.attr('x', gridSize / 2).attr('y', gridSize / 2);

    dotPattern
      .append('circle')
      .attr('cx', gridSize / 2)
      .attr('cy', gridSize / 2)
      .attr('r', dotR)
      .attr('fill', dotColor);

    // apply to your background rect
    svg.select('rect.canvas').attr('fill', `url(#${iid('dot-grid')})`);

    // Diagonal stripe overlay — clinically confirmed diagnoses
    const stripePattern = defs
      .append('pattern')
      .attr('id', iid('dx-confirmed-stripe'))
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 16)
      .attr('height', 16)
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('patternTransform', 'rotate(45)');
    stripePattern
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 8)
      .attr('height', 16)
      .attr('fill', 'white')
      .attr('fill-opacity', 0.55);

    const ped = svg.append('g').attr('class', 'diagram');

    const { nodes, edges, nodesById } = layout;

    // Stamp every union with its engine anchor (offset from the partners'
    // midpoint) so the drag handlers' live refreshes preserve asym-parked /
    // staircase / lane-routed junctions instead of flattening them to the
    // naive midpoint (the G-1-01422 click-flip — see unionAnchors.js).
    stampUnionAnchors(nodesById);

    // --- Viewport anchoring (Phase A) --------------------------------------
    // Before overwriting the node refs, work out how to frame this render:
    //  • proband centre — for the first open of the pedigree (A2);
    //  • reanchorWorldDelta — on a STRUCTURAL re-render (a relative was added/removed,
    //    so the node count changed), how far the node the user was editing moved, so we
    //    keep it fixed on screen (A1). A cosmetic re-render (labels/prefs/drag) has an
    //    unchanged count → no reanchor, and the live pan/zoom is preserved as before.
    // TGCLOUD-1863: the person in context first, then the ARROWED proband, then
    // the structural root (openFrame.js focusPointFor). It used to be the
    // structural root whatever the arrow said, so Make Proband moved the arrow
    // and not the open.
    const openCenter = focusPointFor(nodes, {
      focusId: focusPidRef?.current,
      probandState
    });
    let reanchorWorldDelta = null;
    const structural =
      prevNodeCountRef.current != null &&
      nodes.length !== prevNodeCountRef.current;
    const anchorPid = reanchorPidRef?.current;
    if (structural && anchorPid != null && prevNodesByIdRef.current) {
      const oldN = prevNodesByIdRef.current.get(anchorPid);
      const newN = nodesById.get(anchorPid);
      if (
        oldN &&
        newN &&
        Number.isFinite(oldN.x) &&
        Number.isFinite(newN.x) &&
        Number.isFinite(oldN.y) &&
        Number.isFinite(newN.y)
      ) {
        reanchorWorldDelta = { dx: newN.x - oldN.x, dy: newN.y - oldN.y };
      }
    }
    prevNodeCountRef.current = nodes.length;
    prevNodesByIdRef.current = nodesById;
    // -----------------------------------------------------------------------

    nodesRef.current = nodes; // keep the drag handlers reading the current node array
    // Row pitch for multi-row label blocks. Shared with getLabelPos so a row
    // lands exactly on the slot reserved for it — this used to be 1.4 against a
    // reservation of 1.35, drifting each block N * 0.05 * fs into the one below
    // (TGCLOUD-1610).
    const lineHeight = options.font_size * LABEL_ROW_PITCH;

    // Deceased predicate — single source of truth. On deceased nodes the
    // diagonal slash (y = -x) shares the bottom-left + top-right corners with
    // the proband arrow and carrier symbol, so those annotations are nudged off
    // the diagonal below (only when deceased; alive nodes are unchanged).
    const isDeceased = (d) => !!(d && (d.dod || d.isAlive === 'false'));

    // Top-right annotation lane, resolved ONCE per datum and shared by its three
    // tenants (carrier +/- mark, proxy "P", nomenclature number) so the enlarged
    // mark can't land on top of a neighbour. Pure geometry — see carrierStatusMark.
    const carrierMarkOf = (d) =>
      carrierStatusMarkGeometry(carrierStatusMarkText(d), {
        fontSize: options.font_size,
        symbolSize: config.SYMBOL_SIZE,
        deceased: isDeceased(d),
        adopted: !!(d && d.adopted)
      });
    const laneXOf = (d, hasProxyP) =>
      topRightLaneX({
        mark: carrierMarkOf(d),
        hasProxyP,
        fontSize: options.font_size,
        symbolSize: config.SYMBOL_SIZE
      });

    let node = ped
      .selectAll('.node')
      .data(nodes)
      .enter()
      .append('g')
      .attr('transform', function (d) {
        if (d.y === -Infinity) d.y = 0;
        return 'translate(' + d.x + ',' + d.y + ')';
      })
      .attr('data-selection-pid', (d) => d.pid)
      // Deterministic proband marker for the Copilot walkthrough (D30) — only
      // the chart proband's node group carries it; everything else is null.
      .attr('data-proband', (d) => (isChartProband(d) ? '' : null))
      .style('display', (d) => (d.hidden ? 'none' : null));

    const pienode = node
      .filter(function (d) {
        return !d.hidden && !d.deleted && d.kind !== config.NODE_TYPE.UNION;
      })
      .selectAll('pienode')
      .data(function (d) {
        // set the disease data for the pie plot
        // TGCLOUD-1793 Phase 1 (1504 remediation B4). The segment list and the
        // colour rule now live in a pure module so they can be tested — Canvas
        // is not mountable in jsdom, so this had zero coverage. The fill
        // resolves through the CANONICAL family map with the member's own copy
        // as fallback, which is what makes the node agree with the legend.
        const memberStatuses = (carrier.byMember || {})[d.pid] || {};
        const showCarrierMarks =
          (labelsSet?.showCarrierMarks ??
            options.labelsSet?.showCarrierMarks) !== false;
        let diagnoseData = buildDiagnoseSegments({
          diagnoses: d.diagnoses,
          memberStatuses,
          diseaseColorMap,
          labelsSet: options.labelsSet,
          showCarrierMarks
        });

        let cancers = diagnoseData.map(() => 1);
        if (diagnoseData.length === 0) cancers = [1];
        return [
          cancers.map(function (val) {
            return {
              cancer: val,
              ncancers: diagnoseData.length,
              diagnoseData: diagnoseData,
              id: d.pid,
              gender: d.gender,
              genderShape: genderIconMap(d),
              proband: isChartProband(d),
              hidden: d.hidden,
              affected: d.affected,
              exclude: d.exclude,
              isHide: d.isHide
            };
          })
        ];
      })
      .enter()
      .append('g');

    const arcGen = d3.arc().innerRadius(0).outerRadius(config.SYMBOL_SIZE);

    pienode
      .selectAll('path')
      .data(
        d3.pie().value(function (d) {
          return d.cancer;
        })
      )
      .enter()
      .append('path')
      .attr('clip-path', function (d) {
        return 'url(#' + iid(d.data.id) + ')';
      }) // clip the rectangle
      .attr('class', 'pienode')
      .attr('d', arcGen)
      .style('fill', function (d, i) {
        if (d.data.ncancers === 0 || d.data.isHide) {
          return colors.shade;
        }
        const seg = d.data.diagnoseData[i];
        // Carrier segments take the opaque node background (not transparent, so
        // relationship lines behind the node don't show through); the bold
        // perimeter conveys carrier status.
        if (seg && seg.status && seg.status !== 'affected') return colors.shade;
        return seg ? seg.color : colors.shade;
      });

    // Stripe overlay pass — same arcs, only fill confirmed segments
    pienode
      .selectAll('path.confirmed-overlay')
      .data(
        d3.pie().value(function (d) {
          return d.cancer;
        })
      )
      .enter()
      .append('path')
      .attr('clip-path', function (d) {
        return 'url(#' + iid(d.data.id) + ')';
      })
      .attr('class', 'confirmed-overlay')
      .attr('d', arcGen)
      .attr('fill', function (d, i) {
        if (d.data.ncancers === 0 || d.data.isHide) return 'none';
        return d.data.diagnoseData[i]?.clinicallyConfirmed &&
          labelOpts.confirmedDiagnosis !== false
          ? `url(#${iid('dx-confirmed-stripe')})`
          : 'none';
      })
      .attr('stroke', 'none')
      .style('pointer-events', 'none');

    node
      .append('path')
      .attr('shape-rendering', 'geometricPrecision')
      .attr('class', 'te')
      .attr(
        'd',
        d3
          .symbol()
          .size((d) => nodeSymbolArea(d))
          .type((d) => nodeSymbolType(d))
      )
      .style('fill', function (d) {
        if (d.kind === config.NODE_TYPE.UNION) return '#ffffff';
        return '#ffffff00';
      })
      .style('stroke', function (d) {
        return '#000';
      })
      .style('pointer-events', function (d) {
        // Ensure all person nodes (including offset 3rd+ spouses) receive
        // pointer events even though their fill is transparent.
        return d.kind === config.NODE_TYPE.UNION ? 'none' : 'all';
      })
      .attr('stroke-width', 10)
      .on('click', function (e, node) {
        if (!node.deleted) {
          e.stopPropagation();
          markViewTouched();
          let d = d3.select(this.parentNode).datum();
          handleClick(e, d, this); // Here this refer to current selected node
        }
      })
      // Double-click a FETUS symbol → open its record for editing (REQ-H4, and the long-deferred
      // double-click-to-edit item). Only wired for repro nodes: a person's double-click behaviour is
      // deliberately left alone. The single click still runs first and selects the carrier, which is
      // the context the editor opens against, so the two compose rather than conflict.
      .on('dblclick', function (e, node) {
        if (!onReproNodeDoubleClick || node?.deleted) return;
        if (!config.ENABLE_REPRO_NODES) return;
        const d = d3.select(this.parentNode).datum();
        if (!d || !isReproNode(d)) return;
        e.preventDefault();
        e.stopPropagation();
        onReproNodeDoubleClick(d);
      })
      .on('contextmenu', function (e, d) {
        e.preventDefault();
        e.stopPropagation();
        if (d) {
          markViewTouched();
          onNodeClick(e, d);
        }
      });

    node
      .filter(function (d) {
        return !d.hidden;
      })
      .append('clipPath')
      .attr('id', function (d) {
        return iid(d.pid);
      })
      .append('path')
      .attr('class', 'node')
      .attr('data-pid', (d) => d.pid)
      .attr(
        'd',
        d3
          .symbol()
          .size((d) => nodeSymbolArea(d))
          .type((d) => nodeSymbolType(d))
      );

    // Record-status linkage marks are drawn as a teal top-left CORNER BADGE in a
    // later pass (see "Record-status linkage badge" below), gated on the
    // labelsSet.showRecordStatus toggle. The former on-node vertical-line marks
    // were removed here because they collided with the Bennett-2022 carrier
    // vertical-line style; the badge keeps the clinical vocabulary inside the
    // symbol untouched. See PLAN-linked-record-corner-badge.md.

    // Phase 3 — pinned-node indicator. A small accent dot at the BOTTOM-LEFT corner of any
    // node the user has placed in a custom (freehand) position, so manual placements are
    // visible at a glance. customPositions is keyed by uid; unions are never pinned. Drawn
    // inside the render effect (which re-runs via `layout` whenever customPositions changes),
    // so it tracks pin add/clear. Corner chosen to avoid the proxy 'P' (top-right), cascade
    // badge (top-left) and disease badge (bottom-right).
    node
      .filter(
        (d) =>
          d.kind !== config.NODE_TYPE.UNION &&
          d.uid != null &&
          customPositions?.has(d.uid)
      )
      .append('circle')
      .attr('class', 'custom-pos-indicator')
      .attr('cx', -config.SYMBOL_SIZE * 0.6)
      .attr('cy', config.SYMBOL_SIZE * 0.6)
      .attr('r', config.SYMBOL_SIZE * 0.15)
      .attr('fill', '#1976d2')
      .attr('stroke', '#fff')
      .attr('stroke-width', config.SYMBOL_SIZE * 0.05);

    // Active carrier mark style, resolved once upstream (PedigreeChart): 'border'
    // (default, all views) or 'verticalLine' (Bennett 2022, single-disease only).
    // NOTE: this governs ONLY the gene→disease carrier marks. The legacy +/-
    // genotype Carrier-Status annotation is a separate, intentionally-untouched
    // pipeline and must NOT be merged into this rendering.
    const carrierMarkStyle = carrierStyle?.activeStyle || 'border';

    // Carrier bold-perimeter overlay (BORDER style) — rendered AFTER the .te
    // border and clip paths so the bold disease-colour perimeter sits ON TOP of
    // the node outline (otherwise the black border obscures it). Tested and
    // obligate carriers render identically; thin radial dividers separate
    // multi-segment carrier nodes. Skipped when the vertical-line style is active
    // (that pass runs later, below).
    if (carrierMarkStyle === 'border') {
      const CARRIER_R = config.SYMBOL_SIZE / Math.sqrt(Math.PI);
      const CARRIER_H = config.SYMBOL_SIZE / 2;
      pienode.each(function (boundArray) {
        const wrapper = boundArray && boundArray[0];
        if (!wrapper || wrapper.ncancers === 0 || wrapper.isHide) return;
        const segs = wrapper.diagnoseData || [];
        const hasCarrier = segs.some((s) => isCarrierMark(s.status));
        if (!hasCarrier) return;

        // Append to the node group (parent), which already contains .te, so the
        // perimeter paints above the border.
        const group = d3.select(this.parentNode);
        const shape =
          wrapper.genderShape === GENDER.male
            ? { type: 'square', h: CARRIER_H }
            : { type: 'circle', r: CARRIER_R };
        const clip = `url(#${iid(wrapper.id)})`;
        const arcs = d3.pie().value(function (s) {
          return s.cancer;
        })(boundArray);

        arcs.forEach(function (arc, i) {
          const seg = segs[i];
          if (!seg) return;

          if (isCarrierMark(seg.status)) {
            let dAttr;
            if (shape.type === 'square') {
              const pts = getSquareSegmentPolyline(arc, shape.h);
              dAttr =
                'M ' +
                pts
                  .map((p, idx) =>
                    idx === 0 ? `${p.x} ${p.y}` : `L ${p.x} ${p.y}`
                  )
                  .join(' ');
            } else {
              dAttr = getCircleSegmentArc(arc, shape.r);
            }
            group
              .append('path')
              .attr('class', 'carrier-perimeter')
              .attr('d', dAttr)
              .attr('fill', 'none')
              .attr('stroke', seg.color || '#000')
              // Bold fixed width (≈0.2·SYMBOL_SIZE), clearly thicker than the
              // base .te border (10) and ≥ the selected border, so it reads
              // boldly and fully covers the original node edge. Drawn after .te
              // so it sits above the border (incl. the selection restyle).
              .attr('stroke-width', config.SYMBOL_SIZE * 0.2)
              // 'butt' (not 'round') so the thick stroke ends flush at the
              // segment boundary and doesn't bleed into the adjacent segment.
              // 'round' join only smooths square-node corners (mid-edge, within
              // the segment), so it's safe to keep.
              .attr('stroke-linecap', 'butt')
              .attr('stroke-linejoin', 'round')
              .attr('clip-path', clip)
              .style('pointer-events', 'none');
          }
        });
      });
    }

    // Marks a text label so the post-pass (after all labels are positioned)
    // can insert a solid background <rect> behind it, masking any relationship
    // line that passes through the label region.
    const markLabelForBackground = (sel) => sel.classed('pedigree-label', true);
    const getDisplayUpn = (person) => {
      if (person.isHide) return '';
      // Reproductive nodes are pedigree elements, not patients — they show no UPN /
      // person number (they also carry none once the data layer stops persisting them
      // as members; until then this suppresses the display).
      if (config.ENABLE_REPRO_NODES && isReproNode(person)) return '';
      if (person.upn) return person.upn;
      const memberId = person.familyMemberId || person.proBandId;
      if (!memberId) return '';
      const suffix = String(memberId).split('-').pop();
      return suffix || '';
    };

    // Centre a selection of text labels (x = -width/2), MEASURE-THEN-WRITE: reading every
    // width first and writing every x after costs ONE forced reflow for the whole pass
    // instead of one per label (each interleaved write invalidates layout for the next
    // getBBox — thousands of reflows in the single pre-show paint at a few hundred nodes).
    // Values are identical: a text's bbox never depends on a sibling's x.
    const centerLabelsX = (sel) => {
      const els = [];
      sel.each(function () {
        els.push(this);
      });
      const widths = els.map((el) => el.getBBox().width);
      els.forEach((el, i) => el.setAttribute('x', -widths[i] / 2));
    };

    // The "+ N more" row under a label whose preset filter withheld records
    // (TGCLOUD-1792).
    //
    // This row is NOT decoration. Labels are captured verbatim in the PNG/PDF
    // export and there is no export-exclusion mechanism for them, so a filtered
    // chart is a clinical document that shows less than the record holds while
    // the sidebar, the CanRisk export and the XML still hold everything. A chart
    // showing three of eleven tests with no indication misrepresents the
    // patient, so the row is drawn whenever anything is hidden and is not
    // separately switchable.
    //
    // Muted and italic so it reads as a note about the list rather than as
    // another record in it. `getLabelPos` reserves its row via the same
    // `hiddenCount`, so it cannot land on the label below.
    const drawOverflowRow = (group, y, hiddenCount) => {
      const label = overflowRowText(hiddenCount);
      if (!label) return;
      const el = group
        .append('text')
        .call(markLabelForBackground)
        .attr('y', y)
        .attr('font-size', `${options.font_size}px`)
        .attr('font-style', 'italic')
        .text(label)
        // The theme's own text colour at reduced opacity, rather than a
        // hard-coded grey: `colors` carries no muted token, and a fixed grey
        // would be invisible on the dark appearance.
        .style('fill', colors.text)
        .style('opacity', 0.75);
      el.attr('x', -el.node().getBBox().width / 2);
    };

    centerLabelsX(
      node
        .filter(function (d) {
          return !d.hidden && options.labelsSet.upn;
        })
        .append('text')
        .call(markLabelForBackground)
        .attr('y', function (d) {
          return getLabelPos(d, renderLabelGeomOptions).upn_y;
        })
        .attr('font-size', `${options.font_size}px`)
        .text((d) => getDisplayUpn(d))
        .style('fill', colors.text)
        .call(applyFmt('upn'))
    );

    centerLabelsX(
      node
        .filter(function (d) {
          return !d.hidden && getAcronym(d);
        })
        .append('text')
        .call(markLabelForBackground)
        .attr('y', function (d) {
          return getLabelPos(d, renderLabelGeomOptions).acronym_y;
        })
        .attr('font-size', `${options.font_size}px`)
        .text((d) => getAcronym(d))
        .style('fill', colors.text)
    );

    centerLabelsX(
      node
        .filter(function (d) {
          return (
            !d.hidden &&
            // An anonymised donor/surrogate never renders a name (role label below)
            !(config.ENABLE_REPRO_NODES && isReproSatelliteNode(d)) &&
            (labelsFor(d).firstName || labelsFor(d).lastName)
          );
        })
        .append('text')
        .call(markLabelForBackground)
        .attr('x', -20)
        .attr('y', function (d) {
          return getLabelPos(d, renderLabelGeomOptions).name_y;
        })
        .attr('font-size', `${options.font_size}px`)
        .style('fill', colors.text)
        .each(function (d) {
          // First and last name as separate, independently-styleable runs.
          if (!d.isHide) appendRuns(d3.select(this), nameRuns(d));
        })
    );

    // Anonymised donor/surrogate role label ("Sperm donor" / "Egg donor" /
    // "Surrogate"), shown in place of the suppressed name. Gated + satellite-only.
    node
      .filter(
        (d) => !d.hidden && config.ENABLE_REPRO_NODES && isReproSatelliteNode(d)
      )
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr('y', (d) => getLabelPos(d, renderLabelGeomOptions).reproRole_y)
      .attr('font-size', `${options.font_size}px`)
      .text((d) => (d.isHide ? '' : reproRoleLabelOf(d)))
      .style('fill', colors.text);

    node
      // .filter(function (d) {
      //   return !d.hidden && options.editText;
      // })
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr('x', 0)
      .attr('y', 20)
      .attr('font-size', `${options.font_size}px`)
      .text((d) => (d.isHide ? '' : d.editText))
      .style('pointer-events', 'none');

    node
      .filter(function (d) {
        return !d.hidden && options.labelsSet.personNote;
      })
      .append('text')
      .call(markLabelForBackground)
      .attr('y', function (d) {
        return getLabelPos(d, renderLabelGeomOptions).com_y;
      })
      // .attr('font-size', options.font_size ? `${options.font_size}px` : '5rem')
      .attr('font-size', `${options.font_size}px`)
      .attr('text-anchor', 'middle')
      .text((d) => (d.isHide ? '' : d.personNote))
      .call(applyFmt('personNote'))
      .call(wrap, noteWrapWidthFor)
      .style('fill', colors.text);

    node
      .filter(function (d) {
        return !d.hidden && options.labelsSet.age;
      })
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr('y', function (d) {
        return getLabelPos(d, renderLabelGeomOptions).age_y;
      })
      .attr('font-size', `${options.font_size}px`)
      .style('fill', colors.text)
      .call(applyFmt('age'))
      .text((d) => {
        if (!d.dob || d.isHide) return '';
        const dobPartial = partialDateFromBackend({
          iso: d.dob,
          estimatedFlag: d.estimatedDob,
          precisionField: d.dobPrecision
        });
        const dodPartial = d.dod
          ? partialDateFromBackend({
              iso: d.dod,
              estimatedFlag: d.estimatedDod,
              precisionField: d.dodPrecision
            })
          : null;
        const result = calculateAgePartial(dobPartial, dodPartial);
        const label = formatAgeDisplay(result);
        return label ? `Age ${label}` : '';
      });
    const pidsWithProxy = new Set(
      nodes.filter((n) => n.isProxy && n.proxyOf != null).map((n) => n.proxyOf)
    );
    node
      .filter(function (d) {
        return !d.hidden && (d.isProxy || pidsWithProxy.has(d.pid));
      })
      .append('text')
      .attr('class', 'proxy-label')
      // Shifted outward when this node also carries a +/- mark (which is drawn
      // closer in and, since it grew, now reaches the "P" at its old x).
      .attr('x', (d) => laneXOf(d, true).proxyX)
      .attr('y', -config.SYMBOL_SIZE * 0.7)
      .attr('font-size', `${options.font_size * PROXY_LABEL_FONT_SCALE}px`)
      .attr('font-weight', 'bold')
      .text('P')
      .style('fill', colors.text)
      .style('pointer-events', 'none');

    // Reproductive interior glyph — P (ongoing pregnancy) / D (donor) / S
    // (surrogate), centred inside the symbol per Bennett. Loss triangles and
    // stillbirths carry a label instead (below), so they get no interior letter.
    // Gated on the flag + a repro predicate → inert for every existing chart.
    node
      .filter(
        (d) =>
          !d.hidden &&
          config.ENABLE_REPRO_NODES &&
          isReproNode(d) &&
          // Any repro node with something to print: the default P/D/S letter, OR a fetus's own
          // pedigree symbol text — which a numbered loss triangle also carries (REQ-G7), so this
          // is no longer limited to ongoing pregnancies and donors.
          reproGlyphLetter(d) !== ''
      )
      .append('text')
      .attr('class', 'repro-glyph')
      .attr('x', 0)
      .attr('y', 0)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', (d) => {
        // Derive the text size from the symbol's OWN area, so a 0.5-scale loss triangle gets
        // proportionate text instead of full-size text overflowing it.
        const linear = Math.sqrt(nodeSymbolArea(d));
        return `${linear * 0.55}px`;
      })
      .attr('font-weight', 'bold')
      .text((d) => reproGlyphLetter(d))
      .style('fill', colors.text)
      .style('pointer-events', 'none');

    // DoB (date of birth)
    node
      .filter((d) => !d.hidden && labelsFor(d).dob && d.dob)
      .append('text')
      .call(markLabelForBackground)
      .attr('y', (d) => getLabelPos(d, renderLabelGeomOptions).dob_y)
      .attr('font-size', `${options.font_size}px`)
      .attr('text-anchor', 'middle')
      .text((d) => (d.isHide ? '' : `DoB: ${fmtDateOnNode(d, 'dob')}`))
      .style('fill', colors.text)
      .call(applyFmt('dob'));

    // DoD (date of death)
    node
      .filter((d) => !d.hidden && labelsFor(d).dod && d.dod)
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr('y', (d) => getLabelPos(d, renderLabelGeomOptions).dod_y)
      .attr('font-size', `${options.font_size}px`)
      .text((d) => (d.isHide ? '' : `DoD: ${fmtDateOnNode(d, 'dod')}`))
      .style('fill', colors.text)
      .call(applyFmt('dod'));

    // Hospital Number
    node
      .filter(
        (d) => !d.hidden && labelsFor(d).hospitalNumber && d.hospitalNumber
      )
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr('y', (d) => getLabelPos(d, renderLabelGeomOptions).hospitalNumber_y)
      .attr('font-size', `${options.font_size}px`)
      .text((d) => (d.isHide ? '' : d.hospitalNumber))
      .style('fill', colors.text)
      .call(applyFmt('hospitalNumber'));

    // Reproductive label lines beneath a pregnancy symbol (Bennett Fig 4). Each is
    // its own single-line pass reading a reserved y from computeLabelLayout; all
    // gated on the flag + a pregnancy predicate → empty selection for every
    // existing chart. reproPreg() reads the pregnancy sub-object off the node.
    const reproPreg = (d) =>
      d.pregnancy || (d.data && d.data.pregnancy) || null;

    // Loss abbreviation beneath the loss triangle. Per Bennett Fig 4 ONLY an ectopic
    // prints text ("ECT"); a spontaneous abortion (SAB) is a bare triangle and a
    // termination (TOP) is marked by the diagonal strike drawn below — so the filter
    // is the render-label predicate, not isPregnancyLoss (no empty <text> for SAB/TOP).
    node
      .filter(
        (d) =>
          !d.hidden &&
          config.ENABLE_REPRO_NODES &&
          reproLabelOn(labelOpts, 'pregnancyOutcome') &&
          !!pregnancyLossRenderLabel(d)
      )
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr('y', (d) => getLabelPos(d, renderLabelGeomOptions).reproLoss_y)
      .attr('font-size', `${options.font_size}px`)
      .text((d) => (d.isHide ? '' : pregnancyLossRenderLabel(d)))
      .style('fill', colors.text)
      .call(applyFmt('pregnancyOutcome'));

    // Stillbirth "SB" beneath the person symbol
    node
      .filter(
        (d) =>
          !d.hidden &&
          config.ENABLE_REPRO_NODES &&
          reproLabelOn(labelOpts, 'pregnancyOutcome') &&
          isStillbirth(d)
      )
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr('y', (d) => getLabelPos(d, renderLabelGeomOptions).stillbirth_y)
      .attr('font-size', `${options.font_size}px`)
      .text((d) => (d.isHide ? '' : 'SB'))
      .style('fill', colors.text)
      .call(applyFmt('pregnancyOutcome'));

    // Gestational age beneath any pregnancy
    node
      .filter(
        (d) =>
          !d.hidden &&
          config.ENABLE_REPRO_NODES &&
          reproLabelOn(labelOpts, 'gestationalAge') &&
          isPregnancyNode(d) &&
          // non-empty check mirrors computeLabelLayout's exists() so a blank
          // gestationalWeeks reserves no row AND draws nothing (no y=0 overlap)
          String(reproPreg(d)?.gestationalWeeks ?? '').trim() !== ''
      )
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr('y', (d) => getLabelPos(d, renderLabelGeomOptions).gestation_y)
      .attr('font-size', `${options.font_size}px`)
      .text((d) => {
        if (d.isHide) return '';
        const p = reproPreg(d);
        // Always weeks. This used to read `p.gestationalUnit`, a field declared on no schema and
        // written by nothing, so the fallback was the only branch that ever ran — it implied a
        // configurable unit that does not exist. Gestational age is captured in whole weeks
        // (`gestationalWeeks`); the weeks+days pair is scan-specific and lives on the obstetric
        // record, not on this label.
        return `${p.gestationalWeeks} weeks`;
      })
      .style('fill', colors.text)
      .call(applyFmt('gestationalAge'));

    // How fetal sex was determined (Bennett: annotate sex-known pregnancies)
    node
      .filter(
        (d) =>
          !d.hidden &&
          config.ENABLE_REPRO_NODES &&
          reproLabelOn(labelOpts, 'fetalSexMethod') &&
          isOngoingPregnancy(d) &&
          reproPreg(d)?.fetalSexKnown &&
          reproPreg(d)?.sexDeterminationMethod
      )
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr(
        'y',
        (d) => getLabelPos(d, renderLabelGeomOptions).sexDetermination_y
      )
      .attr('font-size', `${options.font_size}px`)
      .text((d) => (d.isHide ? '' : reproPreg(d).sexDeterminationMethod))
      .style('fill', colors.text)
      .call(applyFmt('fetalSexMethod'));

    // Affected-pregnancy karyotype / finding
    node
      .filter(
        (d) =>
          !d.hidden &&
          config.ENABLE_REPRO_NODES &&
          reproLabelOn(labelOpts, 'fetusKaryotype') &&
          isPregnancyNode(d) &&
          reproPreg(d)?.karyotype
      )
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr('y', (d) => getLabelPos(d, renderLabelGeomOptions).karyotype_y)
      .attr('font-size', `${options.font_size}px`)
      .text((d) => (d.isHide ? '' : reproPreg(d).karyotype))
      .style('fill', colors.text)
      .call(applyFmt('fetusKaryotype'));

    // Fetus name (opt-in — new output, so default OFF)
    node
      .filter(
        (d) =>
          !d.hidden &&
          config.ENABLE_REPRO_NODES &&
          reproLabelOn(labelOpts, 'fetusName') &&
          isPregnancyNode(d) &&
          String(d.fetusName ?? d.data?.fetusName ?? '').trim() !== ''
      )
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr('y', (d) => getLabelPos(d, renderLabelGeomOptions).fetusName_y)
      .attr('font-size', `${options.font_size}px`)
      .text((d) =>
        d.isHide ? '' : String(d.fetusName ?? d.data?.fetusName).trim()
      )
      .style('fill', colors.text)
      .call(applyFmt('fetusName'));

    // Donor role beside an IDENTIFIED donor (TGCLOUD-1597, opt-in — new output, default OFF).
    // An anonymous donor is a satellite whose role already stands in for its name; this is for the
    // case where the donor is a real person who keeps their own, and it is what makes a donor line
    // crossing the chart readable. donorRoleLabelOf returns '' for anyone who is not one.
    node
      .filter(
        (d) =>
          !d.hidden &&
          config.ENABLE_REPRO_NODES &&
          reproLabelOn(labelOpts, 'donorRole') &&
          !!donorRoleLabelOf(d)
      )
      .append('text')
      .call(markLabelForBackground)
      .attr('text-anchor', 'middle')
      .attr('y', (d) => getLabelPos(d, renderLabelGeomOptions).donorRole_y)
      .attr('font-size', `${options.font_size}px`)
      .text((d) => (d.isHide ? '' : donorRoleLabelOf(d)))
      .style('fill', colors.text)
      .call(applyFmt('donorRole'));

    // External Patient IDs — one line per id, then the "+ N more" overflow row.
    //
    // The rows come from the shared select helper rather than straight off
    // `d.externalPatientIds`, so the preset's limit reaches this label at all
    // (it was offered, saved and badged but never applied) and so the renderer
    // and the row reservation count the same rows (TGCLOUD-1792).
    //
    // The node filter deliberately still reads the RAW array: a limit that hides
    // every id must still draw the overflow row, and gating on the filtered
    // count would skip the node entirely.
    node
      .filter(
        (d) =>
          !d.hidden &&
          labelsFor(d).externalPatientId &&
          Array.isArray(d.externalPatientIds) &&
          d.externalPatientIds.length > 0
      )
      .each(function (d) {
        if (d.isHide) return;
        const group = d3.select(this);
        const baseY = getLabelPos(
          d,
          renderLabelGeomOptions
        ).externalPatientId_y;
        const { items, hiddenCount } = selectExternalPatientIdRows(
          d,
          labelOpts
        );
        items.forEach((id, i) => {
          group
            .append('text')
            .attr('text-anchor', 'middle')
            .attr('y', baseY + i * lineHeight)
            .attr('font-size', `${options.font_size}px`)
            .text(id)
            .style('fill', colors.text)
            .call(applyFmt('externalPatientId'));
        });
        drawOverflowRow(group, baseY + items.length * lineHeight, hiddenCount);
      });

    // Diseases (with age)
    node
      .filter(
        (d) =>
          !d.hidden &&
          labelOpts.diseaseWithAge &&
          Array.isArray(d.diagnoses) &&
          d.diagnoses.length > 0
      )
      .each(function (d) {
        const group = d3.select(this);

        // Shared with the row reservation and auto-spacing, so the visibility
        // filter, the per-type value sort (TGCLOUD-1663) and the preset's
        // per-unit filter (TGCLOUD-1792) cannot differ between them.
        const { items, hiddenCount } = selectDiagnosesRows(d, labelOpts);

        drawOverflowRow(
          group,
          getLabelPos(d, renderLabelGeomOptions).diagnoses_y +
            items.length * lineHeight,
          hiddenCount
        );

        items.forEach((di, i) => {
          const labelTxt = `${di.alias || di.name || ''}${
            di.age ? ` (${di.age})` : ''
          }`;
          const y =
            getLabelPos(d, renderLabelGeomOptions).diagnoses_y + i * lineHeight;
          const gap = 20;

          const textEl = group
            .append('text')
            .call(markLabelForBackground)
            .attr('y', y)
            .attr('font-size', `${options.font_size}px`)
            .text(labelTxt)
            .style('fill', colors.text)
            .call(applyFmt('diseaseWithAge'));

          const bbox0 = textEl.node().getBBox();
          const textWidth = bbox0.width;
          textEl.attr('x', -textWidth / 2);

          if (di.clinicallyConfirmed && labelOpts.confirmedDiagnosis) {
            const r = options.font_size * 0.42;
            // The post-centring right edge is derivable from the pre-centring bbox
            // (the x shift is exactly -width/2), so no second getBBox — the re-read
            // forced an extra reflow per confirmed diagnosis:
            //   newRight = (bbox0.x - width/2) + width = bbox0.x + width/2
            const icx = bbox0.x + textWidth / 2 + 52 + r;
            const icy = y - options.font_size * 0.25;

            // Outer circle
            group
              .append('circle')
              .attr('cx', icx)
              .attr('cy', icy)
              .attr('r', r)
              .attr('stroke', CONFIRMED_TICK_COLOUR)
              .attr('stroke-width', options.font_size * 0.1)
              .attr('fill', 'none')
              .style('pointer-events', 'none');

            // Inner checkmark
            group
              .append('path')
              .attr(
                'd',
                `M${icx - r * 0.5},${icy} L${icx - r * 0.1},${
                  icy + r * 0.42
                } L${icx + r * 0.52},${icy - r * 0.45}`
              )
              .attr('stroke', CONFIRMED_TICK_COLOUR)
              .attr('stroke-width', options.font_size * 0.12)
              .attr('fill', 'none')
              .attr('stroke-linecap', 'round')
              .attr('stroke-linejoin', 'round')
              .style('pointer-events', 'none');
          }
        });
      });

    // Phenotypes
    node
      .filter(function (d) {
        // `hasRowsToDraw`, NOT the filtered row count. This gate used to read
        // `collectPhenotypeSource(...).length > 0` — the rows left AFTER the
        // preset's filter — so a confirmed-only or recency filter that hid every
        // one of a person's phenotypes drew nothing at all: no rows and no
        // "+ 2 more" line, over a row the reservation had already kept. The node
        // then read, on screen and in the PNG/PDF export, as a person with no
        // phenotypes. See hasRowsToDraw in utils.js.
        return (
          !d.hidden &&
          hasRowsToDraw(selectPhenotypeRows(d, labelOpts)) &&
          (options.labelsSet.phenotypesName || options.labelsSet.phenotypesID)
        );
      })
      .each(function (d) {
        // The visibility filter that used to be repeated here now lives in the
        // shared select helper alongside the sort and the preset filter, so the
        // renderer, the reservation and auto-spacing cannot disagree about which
        // rows exist (TGCLOUD-1792).
        const { items: phenotypes, hiddenCount } = selectPhenotypeRows(
          d,
          labelOpts
        );
        const group = d3.select(this);

        drawOverflowRow(
          group,
          getLabelPos(d, renderLabelGeomOptions).phenotypes_y +
            phenotypes.length * lineHeight,
          hiddenCount
        );

        // Iterate over phenotypes and append each on a new line. Absent
        // phenotypes (observed === false) that opt-in to the pedigree get
        // a bold "- Absent" suffix appended via a tspan so the status is
        // readable inline at a glance.
        phenotypes.forEach((p, i) => {
          const namePart = labelOpts.phenotypesName ? p.name : '';
          const idPart = labelOpts.phenotypesID ? `(${p.id})` : '';
          const isAbsent = p.observed === false;

          // Name and ID as independently-styleable runs; absent phenotypes get
          // the bold "- Absent" suffix (always bold).
          const runs = [];
          if (namePart) runs.push({ text: namePart, key: 'phenotypesName' });
          if (namePart && idPart) runs.push({ text: ' ', key: null });
          if (idPart) runs.push({ text: idPart, key: 'phenotypesID' });
          if (isAbsent) {
            runs.push({ text: ' - ', key: null });
            runs.push({ text: 'Absent', key: null, bold: true });
          }

          const textEl = group
            .append('text')
            .call(markLabelForBackground)
            .attr('y', function () {
              const baseY = getLabelPos(d, renderLabelGeomOptions).phenotypes_y;
              return baseY + i * lineHeight;
            })
            .attr('font-size', `${options.font_size}px`)
            .style('fill', colors.text);

          appendRuns(textEl, runs);

          // Centre using the final composed width (must run after runs are
          // appended so the bbox reflects the full label).
          const textWidth = textEl.node().getBBox().width;
          textEl.attr('x', -textWidth / 2);
        });
      });

    // Genotypes
    node
      .filter(function (d) {
        // Same defect and same fix as the phenotype gate above: a filter that
        // withheld every genotype silently emptied the node instead of printing
        // the overflow row. computeLabelLayout reserves the row for that case
        // too, so the line has space to land in.
        return (
          !d.hidden &&
          hasRowsToDraw(selectGenotypeRows(d, labelOpts)) &&
          (options.labelsSet.genotypesName ||
            options.labelsSet.genotypesID ||
            options.labelsSet.genotypeCarrierStatus)
        );
      })
      .each(function (d) {
        // As above: the visibility filter, the value sort and the preset's
        // per-unit filter all come from the one shared helper (TGCLOUD-1792).
        const { items: genotypes, hiddenCount } = selectGenotypeRows(
          d,
          labelOpts
        );
        const group = d3.select(this);

        drawOverflowRow(
          group,
          getLabelPos(d, renderLabelGeomOptions).genotypes_y +
            genotypes.length * lineHeight,
          hiddenCount
        );

        // Iterate over genotypes and append each on a new line. Symbol and
        // ID are independent toggles — the full gene description was a
        // separate option that was removed; symbol + id covers the use case.
        genotypes.forEach((p, i) => {
          // Symbol / ID / carrier-status as independently-styleable runs, built
          // by the shared helper that auto-spacing measures and getLabelPos
          // reserves space for — see buildGenotypeRuns (TGCLOUD-1610).
          const runs = buildGenotypeRuns(p, labelOpts);

          // Nothing to draw for this genotype (all parts toggled off / empty).
          if (!runs.length) return;

          const rowY =
            getLabelPos(d, renderLabelGeomOptions).genotypes_y + i * lineHeight;

          const textNode = group
            .append('text')
            .call(markLabelForBackground)
            .attr('y', rowY)
            .attr('font-size', `${options.font_size}px`)
            .style('fill', colors.text);

          appendRuns(textNode, runs);
          const genoBBox0 = textNode.node().getBBox();
          const genoWidth = genoBBox0.width;
          textNode.attr('x', -genoWidth / 2);

          // One marker per gene label row. Where the laboratory and ClinVar
          // disagree — or where two variants on this gene sit in different
          // severity buckets — the marker is a neutral "≠" that sends the
          // reader to the record, rather than drawing one source's call as
          // though it were settled. The tooltip states how many variants the
          // marker speaks for, so a gene row never implies its variants share
          // a single classification.
          const marker = geneMarker(p.variants);
          if (marker) {
            // Post-centring right edge derived from the pre-centring bbox (x shift is
            // exactly -width/2) — avoids a second getBBox reflow per badge.
            const dotRadius = Math.max(3, options.font_size * 0.35);
            const gap = 4;
            const dotCx = genoBBox0.x + genoWidth / 2 + gap + dotRadius;
            const dotCy = rowY - options.font_size * 0.35;

            const tooltip =
              marker.aggregated && marker.markedCount > 1
                ? `${marker.label} — most significant of ${marker.markedCount} classified variants`
                : marker.label;

            // The primary glyph: either the neutral differ mark, or a dot
            // carrying the leading classification.
            if (marker.kind === MARKER_KIND.differ) {
              const differ = group
                .append('text')
                .attr('x', dotCx)
                .attr('y', dotCy + dotRadius)
                .attr('font-size', `${options.font_size * 0.95}px`)
                .attr('text-anchor', 'middle')
                .attr('font-weight', 700)
                .style('fill', marker.color)
                .style('pointer-events', 'none')
                .text('≠');
              differ.append('title').text(tooltip);
            } else {
              const dot = group
                .append('circle')
                .attr('cx', dotCx)
                .attr('cy', dotCy)
                .attr('r', dotRadius)
                .attr('fill', marker.color)
                .style('pointer-events', 'none');
              dot.append('title').text(tooltip);
            }

            // The clinically-reviewed tick, drawn INDEPENDENTLY of the glyph
            // above. Source disagreement and its review are separate facts: a
            // gene can carry variants in different severity buckets — its own
            // reason for the differ mark — while every source disagreement on
            // it has been reviewed. Tying the tick to the primary signal meant
            // a reviewed discrepancy showed nothing on the chart whenever
            // something else won the row.
            //
            // Same circle-and-check as a clinically confirmed diagnosis: the
            // claim is of the same kind, so the symbol is too.
            if (marker.allDisagreementsReviewed) {
              const tickR = options.font_size * 0.34;
              const tickX = dotCx + dotRadius + tickR + 2;
              const tickY = dotCy;
              const tickTitle =
                marker.sourceDisagreements > 1
                  ? `All ${marker.sourceDisagreements} classification discrepancies on this gene have been clinician-reviewed`
                  : 'The classification discrepancy on this gene has been clinician-reviewed';
              group
                .append('circle')
                .attr('cx', tickX)
                .attr('cy', tickY)
                .attr('r', tickR)
                .attr('stroke', CONFIRMED_TICK_COLOUR)
                .attr('stroke-width', options.font_size * 0.1)
                .attr('fill', 'none')
                .style('pointer-events', 'none')
                .append('title')
                .text(tickTitle);
              group
                .append('path')
                .attr(
                  'd',
                  `M${tickX - tickR * 0.5},${tickY} L${tickX - tickR * 0.1},${
                    tickY + tickR * 0.42
                  } L${tickX + tickR * 0.52},${tickY - tickR * 0.45}`
                )
                .attr('stroke', CONFIRMED_TICK_COLOUR)
                .attr('stroke-width', options.font_size * 0.12)
                .attr('fill', 'none')
                .attr('stroke-linecap', 'round')
                .style('pointer-events', 'none');
            }
          }
        });
      });

    // records reviewed
    node
      .filter(function (d) {
        return !d.hidden && d.recordsReviewed;
      })
      .append('path')
      .attr('shape-rendering', 'geometricPrecision')
      .attr(
        'd',
        d3
          .symbol()
          .size(function (d) {
            return options.font_size * 50;
          })
          .type(function (d) {
            return d3.symbolAsterisk;
          })
      )
      .attr('transform', function () {
        const offsetX = config.SYMBOL_SIZE * 0.65;
        const offsetY = config.SYMBOL_SIZE * 0.65;
        return `translate(${offsetX}, ${offsetY})`;
      })
      .style('stroke', function (d) {
        return colors.text;
      })
      .attr('stroke-width', 5);

    // Stacked header + item-list labels (Consultation Date / Diagnosis / Tests /
    // Test Comment). A HEADER row, then ONE ROW PER RECORD, never wrapped
    // (TGCLOUD-1781). They were a single comma-joined line once, which grew
    // without bound and overflowed into neighbouring nodes; the fix for that
    // wrapped each item, which turned a long record into a paragraph. One row
    // per record is the shape a clinician reads. getLabelPos reserves exactly
    // this row count from the same shared labelText helper, so these blocks
    // never overlap the labels stacked below them.
    // IMPORTANT: gate on labelOpts here (not options.labelsSet)
    const blockLineHeight = options.font_size * LABEL_ROW_PITCH; // matches getLabelPos spacing
    STACKED_LABEL_BLOCKS.forEach((blk) => {
      if (!blockEnabled(blk, labelOpts)) return;

      node
        .filter(
          (d) =>
            !d.hidden && !d.isHide && blockLines(blk, d, labelOpts).length > 0
        )
        .each(function (d) {
          const lines = blockLines(blk, d, labelOpts);
          if (!lines.length) return;

          const baseY = getLabelPos(d, renderLabelGeomOptions)[blk.yKey];
          const text = d3
            .select(this)
            .append('text')
            .call(markLabelForBackground)
            .attr('text-anchor', 'middle')
            .attr('y', baseY)
            .attr('font-size', `${options.font_size}px`)
            .style('fill', colors.text);

          // Each line is a set of styled runs. The first run of a line carries
          // x=0 + y (establishing the centered line position, text-anchor=middle
          // anchors the whole chunk); later runs flow inline. Per-run formatting
          // lets Test Name / Genes / Result / Variants / Comment style
          // independently; the line's intrinsic italic (the test comment) is
          // always applied.
          lines.forEach((ln, i) => {
            const lineY = baseY + i * blockLineHeight;
            (ln.runs || []).forEach((run, ri) => {
              const f = fmtFor(run.key);
              const tspan = text.append('tspan').text(run.text);
              if (ri === 0) tspan.attr('x', 0).attr('y', lineY);
              if (ln.italic || run.italic || (f && f.italic)) {
                tspan.attr('font-style', 'italic');
              }
              if (f && f.bold) tspan.attr('font-weight', 'bold');
              // The block's "+ N more" overflow row (labelText.overflowBlockLine)
              // — muted so it reads as a note about the list rather than as
              // another record in it, matching drawOverflowRow's treatment of
              // the diagnosis / phenotype / genotype overflow rows. fill-opacity
              // rather than opacity: this is a tspan inside a shared <text>, and
              // `opacity` on a tspan is not applied by every SVG renderer that
              // the exported, serialised chart passes through.
              if (ln.muted) tspan.style('fill-opacity', 0.75);
            });
          });
        });
    });

    // Label background post-pass — runs once every marked label has its final
    // position, content, and centering applied. Insert a solid <rect> just
    // before each marked text node (SVG document order = z-order, so the rect
    // renders behind the text) sized to the text bbox + padding. This masks any
    // relationship line passing through the label region, including open glyph
    // interiors and inter-character gaps that a paint-order halo can't cover.
    const labelBgPadding = 2;
    // MEASURE-THEN-WRITE (perf, large families): the naive per-label getBBox → insert loop
    // forces a synchronous reflow per label (each inserted rect invalidates layout for the
    // next getBBox) — thousands of reflows at a few hundred nodes, a multi-second stall in
    // the one pre-show paint. Reading every bbox first, then inserting every rect, costs a
    // single reflow. The geometry is identical: SVG has no flow layout, so an inserted
    // rect never changes a text's bbox.
    const labelBgEntries = [];
    node.selectAll('text.pedigree-label').each(function () {
      let bbox;
      try {
        bbox = this.getBBox();
      } catch (_) {
        return;
      }
      if (!bbox || !bbox.width || !bbox.height) return;
      labelBgEntries.push({ el: this, bbox });
    });
    for (const { el, bbox } of labelBgEntries) {
      d3.select(el.parentNode)
        .insert('rect', () => el)
        .attr('class', 'pedigree-label-bg')
        .attr('x', bbox.x - labelBgPadding)
        .attr('y', bbox.y - labelBgPadding)
        .attr('width', bbox.width + labelBgPadding * 2)
        .attr('height', bbox.height + labelBgPadding * 2)
        .attr('fill', colors.background || '#ffffff')
        .style('pointer-events', 'none');
    }

    // Carrier status (+ / - genotype annotation). Deliberately oversized
    // relative to the labels — it is a marker to be caught at a glance while
    // scanning a zoomed-out chart, not a line of text — and hung off the
    // top-right corner OUTSIDE everything else that reaches into it (the symbol,
    // the deceased slash, an adoption bracket, then the proxy "P" and the
    // nomenclature number, which step aside for it). All of that geometry is in
    // carrierStatusMark; nothing positional is decided here.
    node
      .filter(function (d) {
        return !d.hidden && !!carrierStatusMarkText(d);
      })
      .each(function (d) {
        const mark = carrierMarkOf(d);
        if (!mark) return;

        d3.select(this)
          .append('text')
          .attr('class', 'carrier-status-mark')
          .attr('x', mark.x)
          .attr('y', mark.y)
          .attr('text-anchor', 'start')
          .attr('font-size', `${mark.fontSize}px`)
          // Regular weight, explicitly: the size carries it, and bold reads as
          // heavy-handed beside the light symbol outlines (clinician feedback).
          .attr('font-weight', 'normal')
          .text(mark.text)
          .style('fill', colors.text)
          .style('pointer-events', 'none');
      });

    // adopted brackets
    node
      .filter(function (d) {
        return !d.hidden && d.adopted;
      })
      .append('path')
      // Geometry lives in nodeMarkPaths so the 18px menu icon draws the same
      // bracket (TGCLOUD-1644) — see that module's header.
      .attr('d', () => adoptedBracketsPath())
      .style('stroke', function (d) {
        return colors.text;
      })
      .attr('stroke-width', 8)
      .style('stroke-dasharray', function (d) {
        return !d.exclude ? null : '3, 3';
      })
      .style('fill', 'none');

    // Fertility symbol - individual persons with no spouse
    node
      .filter(function (d) {
        return (
          !d.hidden && d.fertility && (!d.spouseIds || d.spouseIds.length === 0)
        );
      })
      .append('path')
      .attr('stroke', function () {
        return colors.text;
      })
      .attr('stroke-width', 10)
      .attr('fill', 'none')
      .attr('d', (d) => fertilityMarkPath(d.fertility));

    // Diagonal line for deceased (bottom-left → top-right; angle fixed by
    // convention). Extent shortened from 0.8 to 0.62·S so the slash still fully
    // crosses the symbol (radius ≈0.56·S) but its tips no longer reach deep into
    // the corner-annotation ring shared with the proband arrow + carrier symbol.
    const DECEASED_SLASH = 0.62 * config.SYMBOL_SIZE;
    node
      .filter(function (d) {
        return isDeceased(d) && !d.hidden;
      })
      .append('line')
      // Stable hook for tests/automation (the slash is otherwise an unclassed
      // <line>). Additive metadata only — see TESTPLAN-pedigree-v1.1.md.
      .attr('data-mark', 'deceased')
      .attr('stroke', function (d) {
        return d.isHide ? '' : colors.text;
      })
      .attr('stroke-width', 10)
      .attr('x1', function (_d) {
        return -DECEASED_SLASH;
      })
      .attr('y1', function (_d) {
        return DECEASED_SLASH;
      })
      .attr('x2', function (_d) {
        return DECEASED_SLASH;
      })
      .attr('y2', function (_d) {
        return -DECEASED_SLASH;
      });

    // Termination of pregnancy (TOP): Bennett Fig 4 marks it with a diagonal line
    // struck through the loss triangle. Unlike the symmetric 45° deceased slash, this
    // strike runs PARALLEL to the triangle's left edge (slope −√3, ≈60°) and extends
    // past the apex and base so it crosses the WHOLE symbol — see terminationStrikeEndpoints
    // for the geometry. Flag-gated + TOP-only → empty selection for every existing chart.
    // Child of the node <g> → moves with it on drag (no redraw pass needed).
    if (config.ENABLE_REPRO_NODES) {
      const strike = terminationStrikeEndpoints();
      node
        .filter((d) => isTerminationOfPregnancy(d) && !d.hidden)
        .append('line')
        .attr('data-mark', 'termination')
        .attr('stroke', (d) => (d.isHide ? '' : colors.text))
        .attr('stroke-width', 10)
        .attr('x1', strike.x1)
        .attr('y1', strike.y1)
        .attr('x2', strike.x2)
        .attr('y2', strike.y2);
    }

    const edgesG = ped
      .append('g')
      .attr('class', 'edges')
      .attr('fill', 'none')
      .attr('stroke', '#000')
      .attr('stroke-width', 8)
      .attr('vector-effect', 'non-scaling-stroke')
      .lower();

    const childEdges = edges.filter((e) => {
      if (e.kind !== 'child') return false;
      if (nodesById.get(e.to)?.hidden) return false;
      const fromNode = nodesById.get(e.from);
      if (fromNode?.kind === config.NODE_TYPE.UNION) {
        return (
          !nodesById.get(fromNode.parent1Id)?.hidden &&
          !nodesById.get(fromNode.parent2Id)?.hidden
        );
      }
      return !fromNode?.hidden;
    });

    const spouseEdges = edges.filter(
      (e) =>
        e.kind === 'spouse' &&
        !nodesById.get(e.from)?.hidden &&
        !nodesById.get(e.to)?.hidden
    );

    const twinEdges = edges.filter((e) => {
      if (e.kind !== 'twin-group') return false;
      if (e.twinIds?.every((pid) => nodesById.get(pid)?.hidden)) return false;
      const fromNode = nodesById.get(e.from);
      if (fromNode?.kind === config.NODE_TYPE.UNION) {
        return (
          !nodesById.get(fromNode.parent1Id)?.hidden &&
          !nodesById.get(fromNode.parent2Id)?.hidden
        );
      }
      return !fromNode?.hidden;
    });

    // The apex the hit areas and the highlight aim at. Delegates to the SHARED
    // `twinApex` rather than recomputing it — TGCLOUD-1692 found this had become a
    // fourth copy of the formula, reading `TWIN_DIAGONAL_MEETING_HEIGHT` itself. The
    // drawn line comes from `twinGroupPath`, so any change to the apex (it is now
    // size-aware for sets of three or more) would have left the click target and the
    // highlight tracing a line that is not the one on screen.
    const twinTriangleApex = (e) => {
      const u = nodesById.get(e.from);
      const twinNodes = (e.twinIds || [])
        .map((pid) => nodesById.get(pid))
        .filter(Boolean);
      const apex = twinApex(u, twinNodes);
      return apex ? { centerX: apex.x, meetingY: apex.y } : null;
    };
    const twinDiagonalHitPath = (e, twinNode) => {
      const apex = twinTriangleApex(e);
      if (!apex || !twinNode) return '';
      return `M${apex.centerX},${apex.meetingY} L${twinNode.x},${twinNode.y}`;
    };
    const twinFullLinePath = (e, twinNode) => {
      const u = nodesById.get(e.from);
      const apex = twinTriangleApex(e);
      if (!u || !apex || !twinNode) return '';
      return `M${u.x},${u.y} L${u.x},${apex.meetingY} L${apex.centerX},${apex.meetingY} L${twinNode.x},${twinNode.y}`;
    };
    const twinPidSet = new Set(twinEdges.flatMap((e) => e.twinIds || []));
    // pid -> the apex x of its twin group (where its diagonal joins the bar).
    const twinApexXByPid = new Map();
    twinEdges.forEach((e) => {
      const apex = twinTriangleApex(e);
      if (apex)
        (e.twinIds || []).forEach((pid) =>
          twinApexXByPid.set(pid, apex.centerX)
        );
    });
    const twinChildHitData = twinEdges.flatMap((e) =>
      (e.twinIds || []).map((pid) => ({ from: e.from, to: pid, edge: e }))
    );

    const siblingGroupChildren = new Map(); // from -> Set(childPid)
    const addGroupChild = (from, pid) => {
      if (!siblingGroupChildren.has(from))
        siblingGroupChildren.set(from, new Set());
      siblingGroupChildren.get(from).add(pid);
    };
    childEdges.forEach((e) => addGroupChild(e.from, e.to));
    twinEdges.forEach((e) =>
      (e.twinIds || []).forEach((pid) => addGroupChild(e.from, pid))
    );
    const siblingGroupEdgeData = Array.from(
      siblingGroupChildren,
      ([from, pidSet]) => ({
        kind: 'sibling-group',
        from,
        childrenPids: Array.from(pidSet)
      })
    ).filter((group) => group.childrenPids.length >= 2);

    const edgeSel = edgesG
      .selectAll('path.child')
      .data([...childEdges])
      .enter()
      .append('path')
      .attr('class', (d) => d.kind)
      .attr('stroke-dasharray', function (d) {
        // Adopted-in: the ENTIRE line of descent is dashed (Bennett 2022), via the pure
        // adoptedDescentDash helper; every other edge stays solid (undefined).
        return adoptedDescentDash(nodesById.get(d.to));
      })
      .attr('d', (d) => d.d);

    // Apply hop-overlines (small arcs where a child line crosses another) to the
    // child edges. Previously this ran ONLY during a drag, so hops appeared
    // while dragging but reverted to plain crossings once the node snapped back
    // to its laid-out position. Run it on the static render too.
    const applyChildEdgeHops = (sel) => {
      // TGCLOUD-1741 (relates 1671): arcs are RENDER, so they come from the
      // DRAWN-geometry map — an arc must hop ink, not the optimiser's
      // score-the-fiction geometry (a blocked union's children score their bar
      // 210px from where it is inked; arcs from the scored map floated where
      // nothing crossed, and the ink's real crossings went un-hopped). Every
      // scoring caller keeps the default scored map — counts byte-identical.
      const intersectionMap = detectAllSegmentIntersections(edges, nodesById, {
        drawn: true
      });
      sel.attr('d', (d) => {
        const intersections = intersectionMap.get(`${d.from}-${d.to}`);
        if (!intersections || intersections.length === 0) return d.d;
        const u = nodesById.get(d.from);
        const c = nodesById.get(d.to);
        if (!u || !c) return d.d;
        const segments = decomposePathIntoSegments(d, nodesById, {
          drawn: true // rebuild on the RENDERED geometry (1577/1578 score-draw split)
        });
        return createPathWithHooksAtSegments(
          { x: u.x, y: u.y },
          { x: c.x, y: c.y },
          segments,
          intersections
        );
      });
    };
    applyChildEdgeHops(edgeSel);

    // Reproductive descent — the solid line of descent from a gamete donor to
    // each offspring (Bennett Fig 5). Its own selection so the child-edge dashing
    // pass above never touches it; it inherits edgesG's solid #000 / width-8
    // stroke (no stroke-dasharray). The layout only emits 'repro-descent' edges
    // when ENABLE_REPRO_NODES is on, so this selection is empty for every existing
    // chart. No mating line to suppress: a donor carries no spouseIds, so no
    // spouse edge is ever emitted. The crossing detector ignores this edge kind
    // (decompose returns [] for it), so it can't perturb existing hop-overs.
    const reproDescentEdges = edges.filter(
      (e) =>
        e.kind === 'repro-descent' &&
        !nodesById.get(e.from)?.hidden &&
        !nodesById.get(e.to)?.hidden
    );
    const reproDescentSel = edgesG
      .selectAll('path.repro-descent')
      .data(reproDescentEdges)
      .enter()
      .append('path')
      .attr('class', (d) => d.kind)
      .attr('d', (d) => d.d);

    // Dual-parent adoption (Bennett Fig 3): a SOLID reference connector from an adopted-in child
    // to its in-graph BIOLOGICAL parents (the dashed ADOPTIVE descent above is unchanged). Now a
    // FIRST-CLASS 'bio-connector' edge emitted by the layout (like 'repro-descent'), so it follows
    // a dragged node live (updateEdgesAndBarsForNode) and carries a hit-area for hover. Gated on
    // the flag + only present when bioFatherId/bioMotherId are linked → this selection is empty
    // (byte-identical render) for every existing chart. from = child; to/coPid = the bio parents;
    // the path aims at their midpoint. Straight line — crossing-aware routing is deferred.
    const bioConnectorEdges = edges.filter(
      (e) =>
        e.kind === 'bio-connector' &&
        !nodesById.get(e.from)?.hidden &&
        !nodesById.get(e.to)?.hidden &&
        !(e.coPid != null && nodesById.get(e.coPid)?.hidden)
    );
    const bioConnectorSel = edgesG
      .selectAll('path.bio-connector')
      .data(bioConnectorEdges)
      .enter()
      .append('path')
      .attr('class', 'bio-connector')
      .attr('d', (d) => d.d)
      .style('fill', 'none')
      .style('stroke', colors.text)
      .attr('stroke-width', 8);

    const setBioConnectorHighlight = (edgeData, highlighted) => {
      bioConnectorSel
        .filter(
          (edge) => edge.from === edgeData.from && edge.to === edgeData.to
        )
        .attr('stroke-width', highlighted ? 16 : 8);
    };

    const bioConnectorHitSel = edgesG
      .selectAll('path.bio-connector-hit')
      .data(bioConnectorEdges)
      .enter()
      .append('path')
      .attr('class', 'bio-connector-hit')
      .attr('d', (d) => d.d)
      .attr('fill', 'none')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 30)
      .style('cursor', 'context-menu')
      .on('mouseenter', function (_e, d) {
        setBioConnectorHighlight(d, true);
      })
      .on('mouseleave', function (_e, d) {
        setBioConnectorHighlight(d, false);
      })
      .on('contextmenu', function (e, d) {
        e.preventDefault();
        e.stopPropagation();
        if (onEdgeClick) {
          onEdgeClick(e, { ...d, kind: 'bio-connector-line' });
        }
      });

    const lowerSegPath = (uNode, cNode) => {
      const midY = (uNode.y + cNode.y) / 2;
      return `M${cNode.x},${midY} L${cNode.x},${cNode.y}`;
    };

    const sibGroupPath = (fromNode, childNodes) => {
      if (!fromNode || childNodes.length < 2) return '';

      const midY = fromNode.sibLaneY ?? sibBarMidY(fromNode.y, childNodes);
      const xs = childNodes.map((node) => node.x);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      return `M${fromNode.x},${fromNode.y} L${fromNode.x},${midY} L${minX},${midY} L${maxX},${midY}`;
    };

    const sibGroupFullPath = (fromNode, childNodes) => {
      if (!fromNode || childNodes.length < 2) return '';

      const midY = fromNode.sibLaneY ?? sibBarMidY(fromNode.y, childNodes);
      const xs = childNodes.map((node) =>
        twinApexXByPid.has(node.pid) ? twinApexXByPid.get(node.pid) : node.x
      );
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);

      const childDrops = childNodes
        .filter((node) => !twinPidSet.has(node.pid))
        .map((node) => `M${node.x},${midY} L${node.x},${node.y}`)
        .join(' ');

      return `M${fromNode.x},${fromNode.y} L${fromNode.x},${midY} L${minX},${midY} L${maxX},${midY} ${childDrops}`;
    };

    const setChildEdgeHighlight = (edgeData, highlighted) => {
      edgeSel
        .filter(
          (edge) => edge.from === edgeData.from && edge.to === edgeData.to
        )
        .attr('stroke', highlighted ? '#1976d2' : '#000')
        .attr('stroke-width', highlighted ? 16 : 8);
    };

    const childHitSel = edgesG
      .selectAll('path.child-hit')
      .data([...childEdges])
      .enter()
      .append('path')
      .attr('class', 'child-hit')
      .attr('d', (d) => {
        const u = nodesById.get(d.from);
        const c = nodesById.get(d.to);
        if (!u || !c) return d.d;
        return lowerSegPath({ x: u.x, y: u.y }, { x: c.x, y: c.y });
      })
      .attr('fill', 'none')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 30)
      .style('cursor', 'context-menu')
      .on('mouseenter', function (_e, d) {
        setChildEdgeHighlight(d, true);
      })
      .on('mouseleave', function (_e, d) {
        setChildEdgeHighlight(d, false);
      })
      .on('contextmenu', function (e, d) {
        e.preventDefault();
        e.stopPropagation();
        if (onEdgeClick) onEdgeClick(e, d);
      });

    const siblingGroupHighlightSel = edgesG
      .selectAll('path.sibling-group-highlight')
      .data(siblingGroupEdgeData)
      .enter()
      .append('path')
      .attr('class', 'sibling-group-highlight')
      .attr('d', (d) => {
        const fromNode = nodesById.get(d.from);
        const childNodes = d.childrenPids
          .map((pid) => nodesById.get(pid))
          .filter(Boolean);
        return sibGroupFullPath(fromNode, childNodes);
      })
      .attr('fill', 'none')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 4)
      .attr('vector-effect', 'non-scaling-stroke')
      .style('pointer-events', 'none');

    const setSiblingGroupHighlight = (groupData, highlighted) => {
      siblingGroupHighlightSel
        .filter((group) => group.from === groupData.from)
        .attr('stroke', highlighted ? '#1976d2' : 'transparent');
      edgesG
        .selectAll('path.twin-diagonal-highlight')
        .filter((d) => d && d.from === groupData.from)
        .attr('stroke', highlighted ? '#1976d2' : 'transparent');
    };

    const siblingGroupHitSel = edgesG
      .selectAll('path.sibling-group-hit')
      .data(siblingGroupEdgeData)
      .enter()
      .append('path')
      .attr('class', 'sibling-group-hit')
      .attr('d', (d) => {
        const fromNode = nodesById.get(d.from);
        const childNodes = d.childrenPids
          .map((pid) => nodesById.get(pid))
          .filter(Boolean);
        return sibGroupPath(fromNode, childNodes);
      })
      .attr('fill', 'none')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 30)
      .style('cursor', 'context-menu')
      .on('mouseenter', function (_e, d) {
        setSiblingGroupHighlight(d, true);
      })
      .on('mouseleave', function (_e, d) {
        setSiblingGroupHighlight(d, false);
      })
      .on('contextmenu', function (e, d) {
        e.preventDefault();
        e.stopPropagation();
        if (onEdgeClick) onEdgeClick(e, d);
      });

    const spouseEdgeSel = edgesG
      .selectAll('path.spouse')
      .data(spouseEdges)
      .enter()
      .append('path')
      .attr('class', (d) => d.kind)
      .attr('stroke-dasharray', (d) =>
        d.parentRelationship === 'casual' ? '20 12' : null
      )
      .attr('d', (d) => d.d);

    const setSpouseEdgeHighlight = (edgeData, highlighted) => {
      spouseEdgeSel
        .filter(
          (edge) => edge.from === edgeData.from && edge.to === edgeData.to
        )
        .attr('stroke', highlighted ? '#1976d2' : '#000')
        .attr('stroke-width', highlighted ? 16 : 8);
    };

    const spouseHitSel = edgesG
      .selectAll('path.spouse-hit')
      .data(spouseEdges)
      .enter()
      .append('path')
      .attr('class', 'spouse-hit')
      .attr('d', (d) => d.d)
      .attr('fill', 'none')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 30)
      .style('cursor', 'context-menu')
      .on('mouseenter', function (_e, d) {
        setSpouseEdgeHighlight(d, true);
      })
      .on('mouseleave', function (_e, d) {
        setSpouseEdgeHighlight(d, false);
      })
      .on('contextmenu', function (e, d) {
        e.preventDefault();
        e.stopPropagation();

        const fromNode = nodesById.get(d.from);
        const toNode = nodesById.get(d.to);

        if (!fromNode || !toNode) return;

        const leftPid = fromNode.x <= toNode.x ? d.from : d.to;
        const rightPid = fromNode.x <= toNode.x ? d.to : d.from;

        if (onEdgeClick) {
          onEdgeClick(e, {
            ...d,
            kind: 'spouse-line',
            leftPid,
            rightPid
          });
        }
      });

    // Fertility symbol — all couples with a fertility value set
    const fertilitySpouseEdges = spouseEdges.filter((e) => {
      const fromNode = nodesById.get(e.from);
      const toNode = nodesById.get(e.to);
      if (!fromNode || !toNode) return false;

      const fertility =
        fromNode.spouseRelationships?.[e.to]?.fertility ||
        toNode.spouseRelationships?.[e.from]?.fertility;
      return !!fertility;
    });

    // Fertility glyph geometry, shared by the initial draw and the live drag
    // re-bake (the glyph used to be drawn once and never refreshed — dragging
    // a couple with a fertility value left the glyph stranded at the old spot).
    const coupleFertilityPath = (e) => {
      const fromNode = nodesById.get(e.from);
      const toNode = nodesById.get(e.to);
      if (!fromNode || !toNode) return '';
      const fertility =
        fromNode.spouseRelationships?.[e.to]?.fertility ||
        toNode.spouseRelationships?.[e.from]?.fertility;

      const [id1, id2] = [e.from, e.to].sort();
      const unionNode = nodesById.get(`union-${id1}-${id2}`);
      const midX = unionNode ? unionNode.x : (fromNode.x + toNode.x) / 2;
      const dy = unionNode ? unionNode.y : (fromNode.y + toNode.y) / 2;
      const len = config.SYMBOL_SIZE * 0.5;
      const drop = config.SYMBOL_SIZE * 1.4;
      const gap = config.SYMBOL_SIZE * 0.25;

      if (fertility === 'fertile') {
        return `M ${midX - len} ${dy + drop} H ${
          midX + len
        } M ${midX} ${dy} V ${dy + drop}`;
      } else if (fertility === 'infertile') {
        return (
          `M ${midX - len} ${dy + drop} H ${midX + len}` +
          ` M ${midX} ${dy} V ${dy + drop}` +
          ` M ${midX - len} ${dy + drop + gap} H ${midX + len}`
        );
      }
      return '';
    };
    const fertilitySel = edgesG
      .selectAll('path.couple-fertility')
      .data(fertilitySpouseEdges)
      .enter()
      .append('path')
      .attr('class', 'couple-fertility')
      .attr('d', coupleFertilityPath);

    const twinEdgeSel = edgesG
      .selectAll('path.twin-group')
      .data(twinEdges)
      .enter()
      .append('path')
      .attr('class', (d) => d.kind)
      .attr('d', (d) => d.d)
      .style('pointer-events', 'none');

    const allUnknownMarkers = twinEdges.flatMap((e) =>
      (e.unknownMarkers || []).map((m) => ({ ...m, edgeId: e.from }))
    );

    const twinUnknownSel = edgesG
      .selectAll('text.twin-unknown')
      .data(allUnknownMarkers)
      .enter()
      .append('text')
      .attr('class', 'twin-unknown')
      .attr('x', (d) => d.x)
      .attr('y', (d) => d.y)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '5rem')
      .attr('fill', '#000')
      .attr('stroke', 'none')
      .text('?');

    const twinDiagHighlightSel = edgesG
      .selectAll('path.twin-diagonal-highlight')
      .data(twinChildHitData)
      .enter()
      .append('path')
      .attr('class', 'twin-diagonal-highlight')
      .attr('d', (d) => twinFullLinePath(d.edge, nodesById.get(d.to)))
      .attr('fill', 'none')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 4)
      .attr('vector-effect', 'non-scaling-stroke')
      .style('pointer-events', 'none');

    const setTwinDiagHighlight = (from, to, highlighted) =>
      twinDiagHighlightSel
        .filter((d) => d.from === from && d.to === to)
        .attr('stroke', highlighted ? '#1976d2' : 'transparent');

    const twinChildHitSel = edgesG
      .selectAll('path.twin-child-hit')
      .data(twinChildHitData)
      .enter()
      .append('path')
      .attr('class', 'twin-child-hit')
      .attr('d', (d) => twinDiagonalHitPath(d.edge, nodesById.get(d.to)))
      .attr('fill', 'none')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 30)
      .style('cursor', 'context-menu')
      .on('mouseenter', function (_e, d) {
        setTwinDiagHighlight(d.from, d.to, true);
      })
      .on('mouseleave', function (_e, d) {
        setTwinDiagHighlight(d.from, d.to, false);
      })
      .on('contextmenu', function (e, d) {
        e.preventDefault();
        e.stopPropagation();
        if (onEdgeClick)
          onEdgeClick(e, { kind: 'child', from: d.from, to: d.to });
      });

    function updateEdgesAndBarsForNode(pid) {
      // spouse edges
      spouseEdgeSel.attr('d', (d) => {
        const u = nodesById.get(d.from);
        const c = nodesById.get(d.to);
        if (!u || !c) return d.d; // one missing endpoint must not abort the whole refresh

        const uNode = { x: u.x, y: u.y };
        const cNode = { x: c.x, y: c.y };

        // d.laneY: a lane-routed marriage bar (stretched cross-link) must keep
        // its lane on live re-bakes, or a drag/click flattens it onto the row.
        let path = spousePath(
          uNode,
          cNode,
          d.parentRelationship,
          d.isConsanguineous,
          d.laneY
        );

        // Write the re-baked path back to the DATUM, not a local copy: the SVG
        // is periodically rebuilt from `layout` (label/analysis/link-status
        // churn) and repaints every edge from `d.d` — a stale datum stranded a
        // dragged couple's marriage bar at its pre-drag spot (Tim, G-8-00018
        // proxy pair, 2026-08-12). The shared object also feeds the hit path
        // and the analysis-pathway overlay.
        d.d = path;
        return path;
      });

      // Fertility glyphs hang off the couple midpoint/union — follow the drag.
      fertilitySel.attr('d', coupleFertilityPath);

      spouseHitSel.attr('d', (d) => {
        const u = nodesById.get(d.from);
        const c = nodesById.get(d.to);
        if (!u || !c) return d.d;

        const uNode = { x: u.x, y: u.y };
        const cNode = { x: c.x, y: c.y };

        return spousePath(
          uNode,
          cNode,
          d.parentRelationship,
          d.isConsanguineous,
          d.laneY
        );
      });

      // Render map = DRAWN geometry (TGCLOUD-1741, see applyChildEdgeHops):
      // live-drag arcs must hop the ink, same as the static render's.
      const intersectionMap = detectAllSegmentIntersections(edges, nodesById, {
        drawn: true
      });

      edgeSel.attr('d', (d) => {
        const u = nodesById.get(d.from);
        const c = nodesById.get(d.to);
        if (!u || !c) return d.d; // guard: never abort the refresh mid-drag

        const uNode = { x: u.x, y: u.y };
        const cNode = { x: c.x, y: c.y };

        // check if this edge has intersections
        const edgeKey = `${d.from}-${d.to}`;
        const intersections = intersectionMap.get(edgeKey);

        let path;
        if (intersections && intersections.length > 0) {
          //create path with hooks
          const segments = decomposePathIntoSegments(d, nodesById, {
            drawn: true // rebuild on the RENDERED geometry (1577/1578 score-draw split)
          });
          path = createPathWithHooksAtSegments(
            uNode,
            cNode,
            segments,
            intersections
          );
        } else {
          // d.laneY (set only for a displaced sibship / a de-merged sibling
          // line) routes the connector on its lane — the same route the
          // initial draw used.
          path = childPath(uNode, cNode, d.laneY);
        }

        // Datum write-back (see the spouse re-bake above): rebuilds repaint
        // from d.d, so a local copy strands the drop at its pre-drag spot.
        d.d = path;
        return path;
      });

      childHitSel.attr('d', (d) => {
        const u = nodesById.get(d.from);
        const c = nodesById.get(d.to);
        if (!u || !c) return d.d;
        return lowerSegPath({ x: u.x, y: u.y }, { x: c.x, y: c.y });
      });

      // Reproductive descent tracks a dragged donor/offspring — same geometry as the
      // initial layout, recomputed live so it follows either node. Passes the live nodes
      // (so the endpoints re-attach at the symbol outlines) and, for a shared-sibship
      // donor line (Bennett 5.1c), the carrier so it re-aims at the sibship-bar corner.
      reproDescentSel.attr('d', (d) => {
        const u = nodesById.get(d.from);
        const c = nodesById.get(d.to);
        if (!u || !c) return d.d;
        const carrier =
          d.sibBarCarrierPid != null ? nodesById.get(d.sibBarCarrierPid) : null;
        // A twin pregnancy's drop forks at an apex, not a sibship bar — recompute that apex from
        // the live nodes so the donor line stays on the fork while anything is dragged. Same
        // stored-pid contract as sibBarCarrierPid.
        let apex = null;
        if (d.twinApexUnionPid != null && Array.isArray(d.twinApexPids)) {
          const union = nodesById.get(d.twinApexUnionPid);
          const twins = d.twinApexPids
            .map((p) => nodesById.get(p))
            .filter(Boolean);
          if (union && twins.length) apex = twinApex(union, twins);
        }
        return reproDescentPath(u, c, carrier, apex);
      });

      // Biological adoption connector follows a dragged child OR bio parent — recompute from the
      // live nodes so both the visible line and its hit-area stay attached (Bennett Fig 3).
      const refreshBioConnector = (sel) =>
        sel.attr('d', (d) => {
          const c = nodesById.get(d.from);
          const p1 = nodesById.get(d.to);
          const p2 = d.coPid != null ? nodesById.get(d.coPid) : null;
          if (!c || !p1) return d.d;
          return bioConnectorPath(c, [p1, p2]);
        });
      refreshBioConnector(bioConnectorSel);
      refreshBioConnector(bioConnectorHitSel);

      siblingGroupHighlightSel.attr('d', (d) => {
        const fromNode = nodesById.get(d.from);
        const childNodes = d.childrenPids
          .map((childPid) => nodesById.get(childPid))
          .filter(Boolean);
        return sibGroupFullPath(fromNode, childNodes);
      });

      siblingGroupHitSel.attr('d', (d) => {
        const fromNode = nodesById.get(d.from);
        const childNodes = d.childrenPids
          .map((childPid) => nodesById.get(childPid))
          .filter(Boolean);
        return sibGroupPath(fromNode, childNodes);
      });

      // Update twin group paths and markers
      twinEdgeSel.attr('d', (d) => {
        const u = nodesById.get(d.from);

        const twinNodes = d.twinIds.map((twinId) => {
          const node = nodesById.get(twinId);
          return { ...node, siblingType: node.siblingType };
        });

        const validTwinNodes = twinNodes.filter((n) => n && n.x !== undefined);

        if (!u || validTwinNodes.length === 0) return d.d;

        // The SIBSHIP, rebuilt from the pids the engine published on this edge, so glyph
        // adjacency is judged the same way mid-drag as it was at layout time
        // (TGCLOUD-1692). Without it a bar would reappear across an ordinary sibling the
        // moment anything in the row moved.
        const cohortNodes = (d.cohortPids || [])
          .map((pid) => nodesById.get(pid))
          .filter((n) => n && n.x !== undefined);

        const uNode = { x: u.x, y: u.y };
        const { path, unknownMarkers: newMarkers } = twinGroupPath(
          uNode,
          validTwinNodes,
          cohortNodes
        );

        // Update marker positions for this edge, matched BY RUN rather than by array
        // index (TGCLOUD-1692). `edgeId` is the union id, so the filter selects the
        // markers of every twin sub-group under that union — two twin pairs among one
        // couple's children share it — and a set can now yield more than one marker of
        // its own. Index matching assigned those the wrong positions. `runKey` is the
        // marker's own member pids, which `twinGroupPath` stamps for exactly this.
        //
        // The key is SORTED. `twinGroupPath` stamps `pids` in DRAWN (x-ascending) order, so
        // dragging one member of a pair past its co-twin turns [1,2] into [2,1] and an
        // order-sensitive key misses — the diagonals and the bar move while the "?" stays
        // put. Index matching got that case right, so an unsorted key was a regression on
        // the very thing it was meant to improve.
        const runKey = (m) =>
          [...(m.pids || [])].sort((a, b) => Number(a) - Number(b)).join(',');
        const byRun = new Map(newMarkers.map((m) => [runKey(m), m]));
        twinUnknownSel
          .filter((m) => m.edgeId === d.from)
          .each(function (m) {
            const fresh = byRun.get(runKey(m));
            if (fresh) {
              m.x = fresh.x;
              m.y = fresh.y;
            }
            // A run that no longer exists — an ordinary sibling was dragged between two
            // co-multiples, breaking it — must not leave its "?" stranded across a
            // non-member. That is the false claim this whole change exists to prevent, so
            // the glyph is hidden rather than left behind. The drag path is an update-only
            // selection (no enter/exit), so the mirror case — a run that newly FORMS
            // mid-drag — cannot get an element until the next relayout; it stays absent,
            // which understates rather than asserts, and is the safe direction.
            this.style.display = fresh ? null : 'none';
          })
          .attr('x', (m) => m.x)
          .attr('y', (m) => m.y);

        // Datum write-back (see the spouse re-bake above).
        d.d = path;
        return path;
      });

      twinChildHitSel.attr('d', (d) =>
        twinDiagonalHitPath(d.edge, nodesById.get(d.to))
      );
      twinDiagHighlightSel.attr('d', (d) =>
        twinFullLinePath(d.edge, nodesById.get(d.to))
      );
    }

    // groupDragNodes: Array of { n, startX, startY } for all selected nodes during a group drag
    let groupDragNodes = null;
    // anchorStart: { x, y } — the drag event's absolute position at drag start
    let anchorStart = null;
    // singleDragStart: snapped {x, y} of the node at drag start (single-node drag) — used by
    // the legacy pixel-pin path's snap-to-grid threshold.
    let singleDragStart = null;
    // singleDragHome: the node's TRUE (unsnapped) layout {x, y} at drag start. The established-
    // order reorder path returns a no-op drag here EXACTLY, so a small drag that doesn't cross a
    // neighbour snaps back to its real position (not the grid-snapped one, which could be up to
    // half a cell off — the "doesn't snap back to its exact position" bug).
    let singleDragHome = null;

    const refreshUnionNodes = (movedPids) => {
      nodesById.forEach((n) => {
        if (n.kind !== config.NODE_TYPE.UNION) return;
        if (!movedPids.has(n.parent1Id) && !movedPids.has(n.parent2Id)) return;
        const p1 = nodesById.get(n.parent1Id);
        const p2 = nodesById.get(n.parent2Id);
        if (!p1 || !p2) return;
        // Midpoint of the spouse line (handles angled 3rd+ spouses) PLUS the
        // engine anchor stamped at layout time — a plain union has anchor 0,
        // an asym-parked/staircase/lane union keeps its intended offset.
        const pos = unionRefreshPosition(n, p1, p2);
        n.x = pos.x;
        n.y = pos.y;
        // Scope to THIS chart's diagram group (not a document-wide d3.select):
        // pids are per-family and reused, so a bare selector would rewrite a
        // node in another mounted chart. The sibling group-drag path below
        // already uses `ped.select` for the same reason.
        ped
          .select(`g[data-selection-pid="${n.pid}"]`)
          .attr('transform', `translate(${n.x},${n.y})`);
      });
    };

    node.call(
      d3
        .drag()
        .filter(function (event) {
          if (spacePanRef?.current) return false;
          return !event.button && !event.ctrlKey;
        })
        .on('start', (event, d) => {
          event.sourceEvent.stopPropagation(); // prevents zoom conflict
          markViewTouched();
          groupDragNodes = null;
          anchorStart = null;
          singleDragStart = null;
          singleDragHome = null;
          const isGroupDrag =
            selectedIdsRef &&
            selectedIdsRef.current.size > 1 &&
            selectedIdsRef.current.has(d.pid);
          if (isGroupDrag) {
            anchorStart = { x: event.x, y: event.y };
            groupDragNodes = [];
            selectedIdsRef.current.forEach((pid) => {
              const n = nodesById.get(pid);
              if (n) groupDragNodes.push({ n, startX: n.x, startY: n.y });
            });
          } else {
            singleDragStart = { x: snapToGrid(d.x), y: snapToGrid(d.y) };
            singleDragHome = { x: d.x, y: d.y };
          }
        })
        .on('drag', function (event, d) {
          if (groupDragNodes) {
            // Group drag: move all selected nodes by the same delta from their start positions
            const ddx = event.x - anchorStart.x;
            const ddy = event.y - anchorStart.y;
            const movedPids = new Set();
            groupDragNodes.forEach(({ n, startX, startY }) => {
              if (n.kind === config.NODE_TYPE.UNION) return;
              n.x = startX + ddx;
              n.y = startY + ddy;
              movedPids.add(n.pid);
              ped
                .select(`g[data-selection-pid="${n.pid}"]`)
                .attr('transform', `translate(${n.x},${n.y})`);
            });
            refreshUnionNodes(movedPids);
            updateEdgesAndBarsForNode(null);
          } else {
            // Single-node drag (existing behaviour)
            d.x += event.dx;
            d.y += event.dy;

            const g = d3.select(this);

            // move the group
            g.attr('transform', `translate(${d.x},${d.y})`);

            // update the label content
            if (d.kind === config.NODE_TYPE.NODE)
              g.select('.x-label').text(Math.round(d.x));

            refreshUnionNodes(new Set([d.pid]));
            updateEdgesAndBarsForNode(d.pid);
          }
        })
        .on('end', function (event, d) {
          // Paint the pinned-node indicator (blue bottom-left dot) immediately. The render
          // effect normally draws it from customPositions, but a pin-only drop deliberately
          // SKIPS the relayout (skipPinRelayoutRef, TGCLOUD-1457) — without this the cue
          // would only appear on the next full render. Mirrors the render effect's attrs.
          const ensurePinDot = (gSel) => {
            if (
              !gSel ||
              gSel.empty() ||
              !gSel.select('circle.custom-pos-indicator').empty()
            )
              return;
            gSel
              .append('circle')
              .attr('class', 'custom-pos-indicator')
              .attr('cx', -config.SYMBOL_SIZE * 0.6)
              .attr('cy', config.SYMBOL_SIZE * 0.6)
              .attr('r', config.SYMBOL_SIZE * 0.15)
              .attr('fill', '#1976d2')
              .attr('stroke', '#fff')
              .attr('stroke-width', config.SYMBOL_SIZE * 0.05);
          };
          if (groupDragNodes) {
            // Group drag end: snap all and batch-save positions
            // Capture before nulling — setCustomPositions updater runs asynchronously
            const capturedNodes = groupDragNodes;
            groupDragNodes = null;
            anchorStart = null;
            // A PURE CLICK on a multi-selected node is a zero-move d3 drag (see the
            // reorder no-op below, G-1-01422): nothing moved, so mutate nothing —
            // this branch used to batch-RE-PIN every selected node on a mere click
            // (pinning unpinned nodes at their current spot + a spurious autosave),
            // which also made the click an undo step that cleared the redo stack.
            const groupMoved = capturedNodes.some(
              ({ n, startX, startY }) =>
                Math.hypot(n.x - startX, n.y - startY) >= 0.5
            );
            if (!groupMoved) return;
            // TGCLOUD-1457: in MOVE mode the drop keeps its EXACT coordinates (no grid
            // quantisation — Kathryn needs precise placement); classic mode keeps the snap.
            const exact = !!freehandModeRef?.current;
            // Pin-only change — the nodes/edges were already moved live (see the layout
            // effect's skipPinRelayoutRef note).
            if (skipPinRelayoutRef) skipPinRelayoutRef.current = true;
            // UNDO checkpoint before the batch-pin write (real movement guaranteed
            // by the guard above).
            if (onHistoryCheckpoint) onHistoryCheckpoint();
            setCustomPositions((prev) => {
              const newMap = new Map(prev);
              const movedPids = new Set();
              capturedNodes.forEach(({ n }) => {
                if (n.kind === config.NODE_TYPE.UNION) return;
                if (!exact) {
                  n.x = snapToGrid(n.x);
                  n.y = snapToGrid(n.y);
                }
                movedPids.add(n.pid);
                const gSel = ped.select(`g[data-selection-pid="${n.pid}"]`);
                gSel.attr('transform', `translate(${n.x},${n.y})`);
                ensurePinDot(gSel);
                newMap.set(n.uid, {
                  uid: n.uid,
                  vx: n.virtualX,
                  vy: n.virtualY,
                  x: n.x,
                  y: n.y,
                  // D2: grid units — the pin survives spacing changes.
                  gx: colUnitsFromX(
                    n.x,
                    layout.cols ?? null,
                    layout.nodeGap,
                    n.virtualY
                  ),
                  gy: rowUnitsFromY(
                    n.y,
                    layout.rows ?? null,
                    layout.levelHeight
                  )
                });
                updateEdgesAndBarsForNode(n.pid);
              });
              refreshUnionNodes(movedPids);
              updateEdgesAndBarsForNode(null);
              return newMap;
            });
          } else {
            // PHASE 2B — drag to REORDER, not to pin. When the established-order feature
            // is on and a PERSON is dragged, a horizontal drag past a same-generation
            // neighbour swaps their within-generation order; the engine reflows spacing
            // from the new order on the next layout. We never pin pixel coordinates here
            // (generation stays relationship-derived — vertical drift is ignored), and we
            // clear any stale pin on the dragged node so it can't override the new rank at
            // render time (buildRenderModel applies pins after layout). Unions fall through
            // to the legacy pin path below.
            // PHASE 3 — Freehand/Move mode: when the toggle is ON, SKIP the reorder branch so
            // the drag falls through to the legacy pixel-pin path (free x+y, incl. between
            // generations). Read the ref inside the handler so the toggle takes effect without
            // re-binding handlers, and check it FIRST so freehand wins over reorder.
            if (
              !freehandModeRef?.current &&
              config.ENABLE_ESTABLISHED_ORDER &&
              setEstablishedOrder &&
              d.kind === config.NODE_TYPE.NODE
            ) {
              // d.x has been mutated to the drop point during the drag; the node's PRE-drag
              // x is in singleDragHome (the TRUE layout x captured at drag start). Pass it so
              // the no-op test measures the real move, and read nodes from the ref so a
              // re-render mid/between drags never feeds the handler a stale array.
              const originX = singleDragHome ? singleDragHome.x : d.x;
              const { order: newOrder, reason: refusal } =
                computeReorderOutcome(nodesRef.current, d, d.x, originX);
              // TGCLOUD-1789 AC2: a drop the chart could not draw honestly is refused
              // HERE, before anything is written, and the user is told why. It used to
              // be accepted, saved, locked and checkpointed, and then quietly undone by
              // the engine seconds later. A reason is only ever set when the node really
              // moved, so this cannot fire on a click.
              if (refusal && onReorderRefused) onReorderRefused(refusal);
              if (!newOrder) {
                // Didn't cross a neighbour — snap the node back to its EXACT layout position
                // (not the grid-snapped one), persist nothing.
                // A PURE CLICK is a zero-move d3 drag (start+end fire, no drag
                // events): nothing was mutated during the gesture, so mutate
                // nothing now — the union/edge re-bake here is what used to
                // flatten engine-parked junctions on every click (G-1-01422).
                const home = singleDragHome;
                const untouched = !home || (d.x === home.x && d.y === home.y);
                if (home) {
                  d.x = home.x;
                  d.y = home.y;
                }
                singleDragStart = null;
                singleDragHome = null;
                if (untouched) return;
                d3.select(this).attr('transform', `translate(${d.x},${d.y})`);
                refreshUnionNodes(new Set([d.pid]));
                updateEdgesAndBarsForNode(d.pid);
                return;
              }
              singleDragStart = null;
              singleDragHome = null;
              // UNDO checkpoint: a crossed-neighbour reorder is a guaranteed real
              // mutation (order + lock + possibly a pin clear — one undo step).
              if (onHistoryCheckpoint) onHistoryCheckpoint();
              setEstablishedOrder((prev) =>
                mergeGenerationOrder(prev, newOrder)
              );
              // TGCLOUD-1909: the row takes its new order on its existing slots
              // NOW, so the swap reads as a swap while the engine lays the family
              // out again (seconds on a large family). The dragged node used to
              // sit wherever it was dropped until then, which read as a mis-drop.
              // The engine's result replaces this when it lands.
              {
                const gen = Math.round(d.virtualY ?? 0);
                const row = nodesRef.current.filter(
                  (n) =>
                    n &&
                    n.kind === config.NODE_TYPE.NODE &&
                    n.uid != null &&
                    Math.round(n.virtualY ?? 0) === gen
                );
                if (row.length === newOrder.length) {
                  const slots = row
                    .map((n) => (n.uid === d.uid ? originX : n.x))
                    .sort((a, b) => a - b);
                  const byUid = new Map(row.map((n) => [n.uid, n]));
                  const moved = new Set();
                  newOrder.forEach((uid, i) => {
                    const n = byUid.get(uid);
                    if (!n || !Number.isFinite(slots[i])) return;
                    if (n.uid !== d.uid && Math.abs(n.x - slots[i]) < 0.5)
                      return;
                    n.x = slots[i];
                    moved.add(n.pid);
                    ped
                      .select(`g[data-selection-pid="${n.pid}"]`)
                      .attr('transform', `translate(${n.x},${n.y})`);
                  });
                  if (moved.size) {
                    refreshUnionNodes(moved);
                    for (const pid of moved) updateEdgesAndBarsForNode(pid);
                  }
                }
              }
              // A manual reorder is an explicit arrangement: from now on the
              // order is enforced as a lock (until Auto-arrange releases it).
              if (setOrderLocked) setOrderLocked(true);
              // Drop any absolute pin on the dragged node (referential no-op when absent,
              // so we don't fire a spurious customPositions autosave). The setEstablishedOrder
              // change re-runs layout and re-renders every node from the new ranks.
              setCustomPositions((prev) => {
                if (!prev.has(d.uid)) return prev;
                const next = new Map(prev);
                next.delete(d.uid);
                return next;
              });
              return;
            }
            // Single-node drag end (legacy pixel-pin behaviour). A UNION is positioned by its
            // parents and is never independently pinned (it has no uid, so it would write a
            // `customPositions[undefined]` entry, and its unsnapped coords would defeat the
            // no-move snap check below). Unions are pointer-events:none so this is normally
            // unreachable, but guard defensively — mirrors the group-drag path's union skip.
            if (d.kind === config.NODE_TYPE.UNION) {
              singleDragStart = null;
              singleDragHome = null;
              return;
            }

            // TGCLOUD-1457 — MOVE mode drops at the EXACT coordinates. The classic path
            // below quantises to 200px grid cells AND discards any drop within one cell
            // (200px) of the start — which made precise placement structurally impossible
            // (Kathryn's "not letting me do what I want at all": a fine adjustment was
            // both snapped away and then deleted as a no-move). In move mode: no grid
            // snap, and only a genuine click-jitter (< 6px) is treated as a no-move —
            // WITHOUT deleting an existing pin.
            if (freehandModeRef?.current) {
              const home = singleDragHome ||
                singleDragStart || { x: d.x, y: d.y };
              const jitter = Math.hypot(d.x - home.x, d.y - home.y);
              singleDragStart = null;
              singleDragHome = null;
              const g = d3.select(this);
              if (jitter < 6) {
                // Accidental wiggle — restore exactly, change nothing. A pure
                // click (zero move) never mutated anything, so skip the
                // union/edge re-bake entirely (see the reorder no-op above).
                if (jitter === 0) return;
                d.x = home.x;
                d.y = home.y;
                g.attr('transform', `translate(${d.x},${d.y})`);
                refreshUnionNodes(new Set([d.pid]));
                updateEdgesAndBarsForNode(d.pid);
                return;
              }
              g.attr('transform', `translate(${d.x},${d.y})`);
              g.select('.x-label').text(Math.round(d.x));
              refreshUnionNodes(new Set([d.pid]));
              ensurePinDot(g);
              // Pin-only change — visuals already updated live; don't relayout (see the
              // layout effect's skipPinRelayoutRef note).
              if (skipPinRelayoutRef) skipPinRelayoutRef.current = true;
              // UNDO checkpoint: past the jitter guards, so this is a real move.
              if (onHistoryCheckpoint) onHistoryCheckpoint();
              setCustomPositions((prev) => {
                const newMap = new Map(prev);
                newMap.set(d.uid, {
                  uid: d.uid,
                  vx: d.virtualX,
                  vy: d.virtualY,
                  x: d.x,
                  y: d.y,
                  // D2: grid units — the pin survives spacing changes.
                  gx: colUnitsFromX(
                    d.x,
                    layout.cols ?? null,
                    layout.nodeGap,
                    d.virtualY
                  ),
                  gy: rowUnitsFromY(
                    d.y,
                    layout.rows ?? null,
                    layout.levelHeight
                  )
                });
                return newMap;
              });
              updateEdgesAndBarsForNode(d.pid);
              return;
            }

            d.x = snapToGrid(d.x);
            d.y = snapToGrid(d.y);

            // Skip update if the node didn't actually move to a new grid cell
            if (
              singleDragStart &&
              d.x === singleDragStart.x &&
              d.y === singleDragStart.y
            ) {
              singleDragStart = null;
              return;
            }

            // Snap back to default position if dropped within the 8
            // surrounding grid cells (i.e. within one gridCell distance).
            const snapBackThreshold = gridCell;
            if (singleDragStart) {
              const dx = d.x - singleDragStart.x;
              const dy = d.y - singleDragStart.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist <= snapBackThreshold) {
                // Reset to original position and remove custom position
                d.x = singleDragStart.x;
                d.y = singleDragStart.y;
                singleDragStart = null;

                const g = d3.select(this);
                g.attr('transform', `translate(${d.x},${d.y})`);
                g.select('.x-label').text(d.x);
                refreshUnionNodes(new Set([d.pid]));

                // UNDO checkpoint before the snap-back pin delete. When the node had
                // no pin the delete is a content no-op — the history's deferred-commit
                // dedupe discards the checkpoint (the closure's customPositions is
                // stale by design, so we can't test has(uid) reliably here).
                if (onHistoryCheckpoint) onHistoryCheckpoint();
                setCustomPositions((prev) => {
                  const newMap = new Map(prev);
                  newMap.delete(d.uid);
                  return newMap;
                });

                updateEdgesAndBarsForNode(d.pid);
                return;
              }
            }
            singleDragStart = null;

            const g = d3.select(this);

            g.attr('transform', `translate(${d.x},${d.y})`);

            g.select('.x-label').text(d.x);

            refreshUnionNodes(new Set([d.pid]));

            // UNDO checkpoint: past the same-grid-cell guard, so a real pin change.
            if (onHistoryCheckpoint) onHistoryCheckpoint();
            setCustomPositions((prev) => {
              const newMap = new Map(prev);
              newMap.set(d.uid, {
                uid: d.uid,
                vx: d.virtualX,
                vy: d.virtualY,
                x: d.x,
                y: d.y,
                // D2: grid units — the pin survives spacing changes.
                gx: colUnitsFromX(
                  d.x,
                  layout.cols ?? null,
                  layout.nodeGap,
                  d.virtualY
                ),
                gy: rowUnitsFromY(d.y, layout.rows ?? null, layout.levelHeight)
              });
              return newMap;
            });

            updateEdgesAndBarsForNode(d.pid);
          }
        })
    );

    const triid = iid('triangle'); /* + makeid(3)*/
    node
      .append('svg:defs')
      .append('svg:marker') // arrow head
      .attr('id', triid)
      .attr('refX', PROBAND_ARROW_MARKER.refX)
      .attr('refY', PROBAND_ARROW_MARKER.refY)
      .attr('markerWidth', PROBAND_ARROW_MARKER.markerWidth)
      .attr('markerHeight', PROBAND_ARROW_MARKER.markerHeight)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', PROBAND_ARROW_HEAD_PATH)
      .style('fill', '#000');

    // Lower-left proband arrow. Shaft, head and both rotation angles come from
    // nodeMarkPaths, which the Make Proband menu icon also draws from.
    const probandShaft = probandArrowLine();
    node
      .append('line')
      .filter((d) => isChartProband(d))
      .attr('x1', probandShaft.x1)
      .attr('y1', probandShaft.y1)
      .attr('x2', probandShaft.x2)
      .attr('y2', probandShaft.y2)
      .attr('stroke', '#000')
      .attr('stroke-width', 4)
      .attr('marker-end', 'url(#' + triid + ')')
      .attr(
        'transform',
        (d) => `rotate(${probandArrowRotation(isDeceased(d))})`
      );

    // initating zoom + pan
    ped.selectAll('text').style('pointer-events', 'none');

    svgRef.current = svg;
    const pedigreeKey = proband?._id ?? proband?.familyId ?? null;
    if (viewInitRef.current !== pedigreeKey) {
      // A different pedigree in this mount: its open starts afresh.
      viewInitRef.current = pedigreeKey;
      framedRef.current = false;
      userTouchedRef.current = false;
    }
    // TGCLOUD-1863 (openFrame.js): frame on the focus person for the first
    // NON-EMPTY layout and for every open-time relayout until the clinician
    // touches the view; never for an empty layout, which used to latch the
    // legacy fallback. Once touched, the live view is restored (and the edited
    // node re-anchored) exactly as before.
    const frame = shouldFrame({
      nodeCount: nodes.length,
      framed: framedRef.current,
      touched: userTouchedRef.current
    });
    const firstReveal = frame && !framedRef.current;
    zoomRef.current = initZoom({}, svg, pedRef, {
      fit: frame,
      center: openCenter,
      inset: viewInsetRef?.current || 0,
      onUserGesture: markViewTouched,
      reanchorWorldDelta: frame ? null : reanchorWorldDelta,
      // Per-family persisted view so, with several charts open at once, one
      // chart's live pan/zoom never overwrites another's restore point.
      zoomKey: proband?.familyId ? `zoom:${proband.familyId}` : undefined
    });
    if (frame) framedRef.current = true;
    // B3: on the first reveal of a pedigree, fade the settled diagram in rather than
    // popping it — a calm hand-off from the loading spinner. Only on first open, so live
    // edits (which keep the view) don't fade on every keystroke.
    if (firstReveal) {
      ped.style('opacity', 0).transition().duration(200).style('opacity', 1);
    }

    // Lasso layer sits on top of everything in screen-space (above the
    // zoom-transformed diagram). pointer-events:none lets clicks pass through
    // to the underlying rect.canvas / nodes.
    const lassoLayer = svg
      .append('g')
      .attr('id', iid('lasso-layer'))
      .attr('pointer-events', 'none');

    const clientToSvg = (cx, cy) => {
      const ctm = svgNode.getScreenCTM();
      if (!ctm) return { x: cx, y: cy };
      const pt = svgNode.createSVGPoint();
      pt.x = cx;
      pt.y = cy;
      const transformed = pt.matrixTransform(ctm.inverse());
      return { x: transformed.x, y: transformed.y };
    };

    const lassoStroke = colors.accent || '#1976d2';

    svg.select('rect.canvas').on('mousedown.lasso', function (e) {
      if (e.button !== 0) return;
      if (!e.shiftKey) return;
      if (lassoEnabledRef && !lassoEnabledRef.current) return;

      const origin = clientToSvg(e.clientX, e.clientY);
      const startClient = { x: e.clientX, y: e.clientY };

      const lassoRect = lassoLayer
        .append('rect')
        .attr('x', origin.x)
        .attr('y', origin.y)
        .attr('width', 0)
        .attr('height', 0)
        .attr('fill', lassoStroke + '14')
        .attr('stroke', lassoStroke)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '5,3')
        .attr('pointer-events', 'none');

      const cleanup = () => {
        if (activeLassoListeners) {
          activeLassoListeners.forEach(({ type, handler }) => {
            window.removeEventListener(type, handler);
          });
          activeLassoListeners = null;
        }
        lassoRect.remove();
      };

      const onMouseMove = (ev) => {
        const cur = clientToSvg(ev.clientX, ev.clientY);
        const x = Math.min(origin.x, cur.x);
        const y = Math.min(origin.y, cur.y);
        const w = Math.abs(cur.x - origin.x);
        const h = Math.abs(cur.y - origin.y);
        lassoRect.attr('x', x).attr('y', y).attr('width', w).attr('height', h);
      };

      const onMouseUp = (ev) => {
        const dx = ev.clientX - startClient.x;
        const dy = ev.clientY - startClient.y;
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) {
          cleanup();
          return;
        }

        const end = clientToSvg(ev.clientX, ev.clientY);
        const transform = d3.zoomTransform(svgNode);
        const k = transform.k || 1;
        const svgMinX = Math.min(origin.x, end.x);
        const svgMaxX = Math.max(origin.x, end.x);
        const svgMinY = Math.min(origin.y, end.y);
        const svgMaxY = Math.max(origin.y, end.y);
        const minX = (svgMinX - transform.x) / k;
        const maxX = (svgMaxX - transform.x) / k;
        const minY = (svgMinY - transform.y) / k;
        const maxY = (svgMaxY - transform.y) / k;

        const selectedPids = nodes
          .filter((n) => {
            if (!n) return false;
            if (n.hidden) return false;
            if (n.gender === 'union' || n.kind === config.NODE_TYPE.UNION) {
              return false;
            }
            return n.x >= minX && n.x <= maxX && n.y >= minY && n.y <= maxY;
          })
          .map((n) => n.pid);

        lassoJustFinished = true;
        cleanup();
        if (onLassoSelect) onLassoSelect(selectedPids);
      };

      const onKeyDown = (ev) => {
        if (ev.key === 'Escape') {
          cleanup();
        }
      };

      activeLassoListeners = [
        { type: 'mousemove', handler: onMouseMove },
        { type: 'mouseup', handler: onMouseUp },
        { type: 'keydown', handler: onKeyDown }
      ];
      activeLassoListeners.forEach(({ type, handler }) => {
        window.addEventListener(type, handler);
      });
    });

    // Carrier VERTICAL LINE style (Bennett 2022, single-disease view) — a
    // separate pass after all node fills/outlines (and the proband arrow /
    // deceased slash) so the line sits on top. It stops at the bottom node edge,
    // so it never crosses the below-node text labels. (Reminder: the +/- genotype
    // Carrier-Status annotation is a separate pipeline and is untouched here.)
    if (carrierMarkStyle === 'verticalLine') {
      const CARRIER_R = config.SYMBOL_SIZE / Math.sqrt(Math.PI);
      const CARRIER_H = config.SYMBOL_SIZE / 2;
      pienode.each(function (boundArray) {
        const wrapper = boundArray && boundArray[0];
        if (!wrapper || wrapper.ncancers === 0 || wrapper.isHide) return;
        const segs = wrapper.diagnoseData || [];
        const carrierSeg = segs.find((s) => isCarrierMark(s.status));
        if (!carrierSeg) return;
        const shape =
          wrapper.genderShape === GENDER.male
            ? { type: 'square', h: CARRIER_H }
            : { type: 'circle', r: CARRIER_R };
        const { top, bottom } = getVerticalLineEnds(shape, 0.275);
        d3.select(this.parentNode)
          .append('line')
          .attr('class', 'carrier-vertical-line')
          .attr('x1', top.x)
          .attr('y1', top.y)
          .attr('x2', bottom.x)
          .attr('y2', bottom.y)
          .attr('stroke', carrierSeg.color || '#000')
          // Heavier than the thin node outline, lighter than the bold border.
          .attr('stroke-width', config.SYMBOL_SIZE * 0.08)
          .attr('stroke-linecap', 'butt')
          .style('pointer-events', 'none');
      });
    }

    // --- Pedigree analysis overlay (Phase 6) -----------------------------
    // Non-destructive cues drawn on top of the genogram: the inheritance
    // pathway (accent overlay on edges leading into affected supporting
    // individuals) and cascade badges (prior % on untested at-risk relatives).
    // Mirrors the carrier-perimeter overlay: appended last, pointer-events
    // none. The drawn cues are gated by the "Show pedigree analysis overlay"
    // toggle (default on); the hover-link is always available when an analysis
    // (Clinical-Insights-gated) is present.
    if (analysis?.family) {
      // Hover-link (canvas → card): hovering a node outlines its analysis
      // card(s). Namespaced so it doesn't clobber other node handlers.
      node
        .on('mouseenter.pahover', (_e, d) => {
          if (d && d.pid != null)
            highlightAnalysisCards(d.pid, true, containerRef.current);
        })
        .on('mouseleave.pahover', () =>
          highlightAnalysisCards(null, false, containerRef.current)
        );
    }
    const showAnalysisOverlay =
      (labelsSet?.showAnalysisOverlay ??
        options.labelsSet?.showAnalysisOverlay) !== false;
    if (showAnalysisOverlay && analysis?.family) {
      const fam = analysis.family;
      const supporting = new Set();
      (fam.inheritancePatterns || []).forEach((p) =>
        (p.supportingIds || []).forEach((id) => supporting.add(String(id)))
      );
      const cascadeByPid = new Map(
        (fam.cascadeCandidates || []).map((c) => [String(c.pid), c])
      );

      // (a) Pathway: accent overlay on child-edges leading into a supporting
      //     (affected) individual — traces the transmission line. Cloned from
      //     the base edge geometry; sits above base edges, behind nodes.
      if (supporting.size) {
        childEdges.forEach((e) => {
          if (!supporting.has(String(e.to)) || !e.d) return;
          edgesG
            .append('path')
            .attr('class', 'analysis-pathway')
            .attr('d', e.d)
            .attr('fill', 'none')
            .attr('stroke', CLINICAL_COLOURS.analysisPathway)
            // Fixed-pixel width via non-scaling-stroke, matching the base
            // relationship lines (which are themselves non-scaling, 8px) so the
            // highlight stays visible and aligned at every zoom. ~1.5× the base
            // line reads as a clear highlight without the heavy band the larger
            // 16px width produced when zoomed out.
            .attr('stroke-width', 12)
            // Flat (butt) ends so the highlight terminates neatly against the
            // horizontal relationship line (and at the node) instead of a round
            // cap that overextends past the junction. Round joins keep any bend
            // in the edge smooth.
            .attr('stroke-linecap', 'butt')
            .attr('stroke-linejoin', 'round')
            .attr('opacity', 0.5)
            .attr('vector-effect', 'non-scaling-stroke')
            .style('pointer-events', 'none');
        });
      }

      // (b) Cascade badge: prior % on each untested at-risk relative, drawn at
      //     the node's upper-LEFT corner (the upper-right is taken by the legacy
      //     +/- variant symbol, which this must not obscure).
      if (cascadeByPid.size) {
        const BR = config.SYMBOL_SIZE * 0.3;
        const BX = -config.SYMBOL_SIZE * 0.52;
        const BY = -config.SYMBOL_SIZE * 0.52;
        node.each(function (d) {
          if (!d || d.hidden) return;
          const cand = cascadeByPid.get(String(d.pid));
          if (!cand) return;
          // `Math.round(prior * 100)` used to sit here. With mode-aware priors
          // an unpriced candidate has a NULL prior, and that expression
          // rendered "NaN%" on the chart. cascadeBadgeText returns null
          // instead, and 12.5% now renders as "12.5%" rather than "13%".
          const badgeText = cascadeBadgeText(cand);
          // Amber saturation tiers (matches the cascade register); purple is
          // reserved for genome/genotype elsewhere. The 'unpriced' tier is a
          // neutral slate, deliberately not a paler amber — it must not read
          // as "a small risk".
          const fill = CASCADE_BADGE_COLORS[getCascadeBadgeTier(cand)];
          const badge = d3
            .select(this)
            .append('g')
            .attr('class', 'cascade-badge')
            .style('pointer-events', 'none');
          badge
            .append('circle')
            .attr('cx', BX)
            .attr('cy', BY)
            .attr('r', BR)
            .attr('fill', fill)
            .attr('stroke', '#fff')
            .attr('stroke-width', 3);
          badge
            .append('text')
            .attr('x', BX)
            .attr('y', BY)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'central')
            .attr('font-size', `${config.SYMBOL_SIZE * 0.24}px`)
            .attr('font-weight', 'bold')
            .attr('fill', '#fff')
            .text(badgeText || '—');
          // An obligate carrier is a factual pedigree deduction, not a
          // probability, so the badge says so on hover rather than showing a
          // bare 100%.
          badge
            .append('title')
            .text(
              cand.derivation?.summary ||
                cand.priorUnavailable?.summary ||
                badgeText ||
                ''
            );
        });
      }
    }

    // --- Standard Pedigree Nomenclature overlay (TGCLOUD-1379) -----------
    // Bennett-style labelling DERIVED from the current layout: Roman generation
    // numerals (I = topmost) in a left gutter + consecutive left-to-right person
    // numbers per generation at each node's top-right. A render-time SNAPSHOT —
    // not persisted, not a UPN; recomputes whenever the layout changes (the
    // effect re-runs on members/labelsSet/customPositions). Lives inside the
    // `.diagram` group so it pans/zooms with content AND is captured + framed by
    // both PNG and PDF export with no export-code changes. Default OFF, so a
    // truthy read (NOT the `!== false` idiom the analysis overlay uses).
    const showNomenclature = Boolean(
      labelsSet?.standardNomenclature ?? options.labelsSet?.standardNomenclature
    );
    if (showNomenclature) {
      const { byPid, generations } = assignNomenclature(nodes);

      // (a) Per-node person number (Arabic index only; the Roman lives in the
      //     gutter). Top-right, outside the carrier ring (~0.66). It is the
      //     OUTERMOST tenant of the top-right lane, so topRightLaneX pushes it
      //     past the legacy +/- variant mark and the proxy "P" whenever the node
      //     shows either; with neither it keeps its historical 0.95/1.15 anchor.
      //     (The records-reviewed asterisk is bottom-right, so it never shares
      //     space with this badge.)
      const NUM_FONT = config.SYMBOL_SIZE * 0.42;
      node.each(function (d) {
        if (!d || d.hidden) return;
        const num = byPid.get(d.pid);
        if (!num) return;
        const hasProxyP = d.isProxy || pidsWithProxy.has(d.pid);
        const bx = laneXOf(d, hasProxyP).numberX;
        const by = -config.SYMBOL_SIZE * (hasProxyP ? 1.15 : 0.95);
        d3.select(this)
          .append('text')
          .attr('class', 'nomenclature-number')
          .attr('x', bx)
          .attr('y', by)
          .attr('text-anchor', 'start')
          .attr('dominant-baseline', 'central')
          .attr('font-size', `${NUM_FONT}px`)
          .attr('font-weight', 'bold')
          .style('fill', colors.text)
          .style('pointer-events', 'none')
          .text(num.index);
      });

      // (b) Left gutter: one Roman numeral per present generation, at the
      //     generation's band y, just left of the leftmost numbered node (same
      //     local coordinate space as the nodes, so it stays aligned and is
      //     included in the export bounding box).
      const numberedXs = nodes.filter((n) => byPid.has(n.pid)).map((n) => n.x);
      if (generations.length && numberedXs.length) {
        const gutterX = Math.min(...numberedXs) - config.NODE_GAP * 0.9;
        const gutter = ped
          .append('g')
          .attr('class', 'nomenclature-gutter')
          .style('pointer-events', 'none');
        generations.forEach((g) => {
          gutter
            .append('text')
            .attr('x', gutterX)
            .attr('y', g.bandY)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'central')
            .attr('font-size', `${config.SYMBOL_SIZE * 0.7}px`)
            .attr('font-weight', 'bold')
            .style('fill', colors.text)
            .text(g.roman);
        });
      }
    }

    // --- Record-status linkage badge -------------------------------------
    // Teal top-left corner badge marking cross-family linked records: FILLED =
    // primary/source, OUTLINE = secondary/read-only copy. Role is derived from
    // linkStatusMap (PersonLink, resolved at render time) — never inferred from
    // missing data. Gated on the labelsSet.showRecordStatus toggle (default off,
    // read via Boolean like the nomenclature overlay). Appended last so it paints
    // above the node and can read the cascade badge's presence for the corner
    // nudge; lives inside .diagram so it pans/zooms and is captured by PNG/PDF
    // export with no export-code change. This is the ONLY interactive/focusable
    // element inside a node group — clicking selects the node and opens the
    // sidebar Record Status panel. See PLAN-linked-record-corner-badge.md.
    const showRecordStatus = Boolean(
      labelsSet?.showRecordStatus ?? options.labelsSet?.showRecordStatus
    );
    if (showRecordStatus) {
      const teal = colors.accent || '#2ba9bf';
      const surface = colors.background || '#ffffff';
      node.each(function (d) {
        // Skip unions, hidden, and PROXY clones: a proxy is a synthetic second
        // rendering of an already-shown person (shared spouse), so badging it
        // would double-mark and double-register a click target for one record —
        // same reason nomenclature excludes proxies.
        if (!d || d.hidden || d.isProxy || d.kind === config.NODE_TYPE.UNION)
          return;
        const gNum = d.proBandId ?? d.familyMemberId;
        const { role, count } = resolveLinkedRecordBadge(linkStatusMap[gNum]);
        if (role === 'none') return;

        // Nudge the corner outward when a cascade badge or an adoption bracket
        // already occupies the top-left corner, so the opaque badge never
        // occludes them. Cascade is detected from the DOM (drawn earlier this
        // pass); the adoption bracket is a plain unclassed <path>, so key off the
        // data field that gates it (see the "adopted brackets" block).
        const hasCascadeBadge = !d3
          .select(this)
          .select('g.cascade-badge')
          .empty();
        const geom = linkedRecordBadgeGeometry(config.SYMBOL_SIZE, {
          hasCascadeBadge,
          hasAdoptionBracket: Boolean(d.adopted)
        });
        const isPrimary = role === 'primary';
        const label = linkedRecordBadgeLabel(role, count);
        const nodeEl = this;

        const badge = d3
          .select(this)
          .append('g')
          .attr('class', 'link-record-badge')
          .attr('role', 'button')
          .attr('tabindex', 0)
          .attr('aria-label', label)
          .style('cursor', 'pointer')
          .style('pointer-events', 'all');

        badge
          .append('rect')
          .attr('x', geom.cx - geom.side / 2)
          .attr('y', geom.cy - geom.side / 2)
          .attr('width', geom.side)
          .attr('height', geom.side)
          .attr('rx', geom.rx)
          .attr('ry', geom.rx)
          .attr('fill', isPrimary ? teal : surface)
          .attr('stroke', isPrimary ? 'none' : teal)
          .attr('stroke-width', isPrimary ? 0 : geom.strokeWidth);

        badge
          .append('path')
          .attr('d', LINK_GLYPH_PATH)
          .attr('transform', linkedRecordGlyphTransform(geom))
          .attr('fill', isPrimary ? '#ffffff' : teal)
          .style('pointer-events', 'none');

        // Native tooltip mirrors the aria-label; works because the group
        // receives pointer events (unlike the pointer-events:none decorations).
        badge.append('title').text(label);

        const activate = (e) => {
          if (e) e.stopPropagation();
          if (typeof onBadgeClick === 'function') {
            const teEl = nodeEl.querySelector('path.te') || nodeEl;
            onBadgeClick(e, d, teEl);
          }
        };
        badge.on('click', activate);
        badge.on('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            activate(e);
          }
        });
      });
    }

    // TGCLOUD-1457 — re-apply the multi-selection highlight after EVERY SVG rebuild. The
    // selection state (ChartBox selectedIds) survives a re-render, but the imperative
    // stroke styling did not: ChartBox's re-apply effect races the ASYNC worker relayout
    // on a 50ms timer and doesn't run at all for establishedOrder-triggered rebuilds — so
    // highlights silently vanished after moving nodes ("lost my ability to shift and
    // highlight multiples"). Painting it here, inside the render effect, makes the
    // highlight exactly as durable as the selection itself.
    if (selectedIdsRef?.current?.size) {
      selectedIdsRef.current.forEach((pid) => {
        ped
          .select(`g[data-selection-pid="${pid}"]`)
          .select('path.te')
          .attr('stroke-width', 20)
          .style('stroke', theme?.accent || '#1976d2');
      });
    }

    // Data overlays (TGCLOUD-1670) — painted LAST in the render effect, for the
    // same reason the selection highlight above is: the SVG is torn down and
    // rebuilt on every pass, and an external effect racing the async relayout is
    // exactly how multi-select highlights used to vanish (TGCLOUD-1457). Reading
    // from a ref keeps the overlay off this effect's dependency list, so a
    // Copilot query repaints without rebuilding the chart or re-running layout.
    //
    // This also runs AFTER initZoom above, so the first-open fit-to-content
    // measures the pedigree, never the rings.
    if (dataOverlaySlotsRef?.current?.length) {
      paintDataOverlays(
        pedRef.current,
        dataOverlaySlotsRef.current,
        dataOverlayStyleRef?.current || {},
        config.SYMBOL_SIZE
      );
    }

    return () => {
      if (activeLassoListeners) {
        activeLassoListeners.forEach(({ type, handler }) => {
          window.removeEventListener(type, handler);
        });
        activeLassoListeners = null;
      }
    };
  }, [
    // members / customPositions / fontsReady now drive the async layout effect
    // above and reach render via `layout`; depending on them here too would
    // re-render against a stale layout.
    layout,
    labelsSet,
    options,
    carrier,
    diseaseColorMap,
    carrierStyle,
    analysis,
    linkStatusMap
  ]);

  // Auto-spacing measures label widths with the Inter web font, which loads
  // async; re-run the layout once it's ready so the measured widths are exact
  // (a pre-load measure falls back to a system font — only a small over/under-
  // estimate, absorbed by the spacing margins, never an overlap). Gated on
  // auto_spacing so the extra re-layout never fires for the default (manual)
  // case, where the font has no effect on spacing.
  useEffect(() => {
    if (fontsReady || !options.auto_spacing) return;
    let cancelled = false;
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!cancelled) setFontsReady(true);
      });
    } else {
      // No Font Loading API (jsdom/SSR): treat fonts as ready so the B1 gate on the
      // first layout can never hang waiting for a promise that will never resolve.
      setFontsReady(true);
    }
    return () => {
      cancelled = true;
    };
  }, [options.auto_spacing, fontsReady]);

  // Separate effect: render lock icon overlays when locked persons change.
  // Uses lockedPersonsVersion (a counter) as trigger so this doesn't re-run the
  // heavy main effect. lockedPersonsRef is a stable ref to the Map.
  useEffect(() => {
    if (!pedRef.current) return;
    const svg = d3.select(pedRef.current).select('svg');
    if (svg.empty()) return;

    // Clear stale lock tooltips
    svg.selectAll('title.lock-title').remove();

    if (!lockedPersonsRef?.current?.size) return;

    // Add tooltip to each locked node (stroke colouring is handled by ChartBox)
    lockedPersonsRef.current.forEach((lockInfo, pid) => {
      const g = svg.select(`g[data-selection-pid="${pid}"]`);
      if (g.empty()) return;
      const pathEl = g.select('path.te');
      if (pathEl.empty()) return;
      pathEl
        .append('title')
        .attr('class', 'lock-title')
        .text(`Locked by ${lockInfo.lockedByName}`);
    });
  }, [lockedPersonsVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Separate effect: repaint data overlays when the match set changes. Keyed on
  // a version counter so a new Copilot answer or a criteria tweak never re-runs
  // the heavy render effect, which on a large family means a full relayout.
  useEffect(() => {
    if (!pedRef.current) return;
    const slots = dataOverlaySlotsRef?.current || [];
    if (!slots.length) {
      clearDataOverlays(pedRef.current);
      return;
    }
    paintDataOverlays(
      pedRef.current,
      slots,
      dataOverlayStyleRef?.current || {},
      config.SYMBOL_SIZE
    );
  }, [dataOverlayVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // The +/- buttons are programmatic zooms (no sourceEvent), so they tell the
  // open-time framing themselves that the clinician has taken the view.
  const zoomIn = useCallback(() => {
    if (!zoomRef.current || !svgRef.current) return;
    markViewTouched();
    svgRef.current
      .transition()
      .duration(300)
      .call(zoomRef.current.scaleBy, 1.2);
  }, [markViewTouched]);

  const zoomOut = useCallback(() => {
    if (!zoomRef.current || !svgRef.current) return;
    markViewTouched();
    svgRef.current
      .transition()
      .duration(300)
      .call(zoomRef.current.scaleBy, 0.8);
  }, [markViewTouched]);

  // Pan so the given person sits at the centre of the UNCOVERED canvas — the
  // reusable primitive behind "recentre", Locate person, the overlay stepping and
  // the reanchor behaviour. Keeps the current zoom unless `scale` is given
  // (Locate person asks for the standard open scale). A centre the CLINICIAN
  // asked for ends the open-time framing; a deep link's own one-shot centre
  // (`silent`) does not, so the open keeps framing on that person.
  const centerOn = useCallback(
    (pid, { scale = null, silent = false } = {}) => {
      if (!zoomRef.current || !svgRef.current || pid == null) return;
      const node = nodesRef.current?.find(
        (n) => String(n?.pid) === String(pid) || String(n?.uid) === String(pid)
      );
      if (!node || !Number.isFinite(node.x) || !Number.isFinite(node.y)) return;
      if (!silent) markViewTouched();
      const current = d3.zoomTransform(svgRef.current.node());
      const k = Number.isFinite(scale) && scale > 0 ? scale : current.k || 0.3;
      const vw = pedRef.current?.offsetWidth || 1140;
      const vh = pedRef.current?.offsetHeight || 860;
      const origin = visibleCenter(vw, vh, viewInsetRef?.current || 0);
      svgRef.current
        .transition()
        .duration(400)
        .call(
          zoomRef.current.transform,
          d3.zoomIdentity
            .translate(origin.x - k * node.x, origin.y - k * node.y)
            .scale(k)
        );
    },
    [pedRef, viewInsetRef, markViewTouched]
  );

  const centerOnProband = useCallback(() => {
    const p = nodesRef.current?.find((n) => n && n.proBandId);
    if (p) centerOn(p.pid);
  }, [centerOn]);

  // UNDO/REDO (v1.1 §2.1): applyLayoutState is about to replace the layout states.
  // A worker settle already in flight would land AFTER the restore, pass the reqId
  // check, re-capture the PRE-undo order and set skipCaptureRelayoutRef — overwriting
  // the restored state and swallowing the restore's own relayout. Make in-flight
  // results stale (the settle callback rejects on reqId mismatch) and clear the
  // one-shot skip flags so the apply's relayout actually runs.
  const invalidateLayout = useCallback(() => {
    layoutReqRef.current += 1;
    skipCaptureRelayoutRef.current = false;
    if (skipPinRelayoutRef) skipPinRelayoutRef.current = false;
  }, [skipPinRelayoutRef]);

  React.useEffect(() => {
    if (canvasActionsRef)
      canvasActionsRef.current = {
        zoomIn,
        zoomOut,
        centerOn,
        centerOnProband,
        invalidateLayout
      };
  }, [
    zoomIn,
    zoomOut,
    centerOn,
    centerOnProband,
    invalidateLayout,
    canvasActionsRef
  ]);

  return (
    <div
      // Copilot guided-walkthrough anchor for the whole pedigree canvas region.
      // Placed on this outer wrapper (not the inner D3 mount div) so it is never
      // touched by D3's SVG injection.
      data-va-anchor="pedigree.canvas"
      style={{
        position: 'relative',
        height: '100%'
      }}
    >
      <Box sx={{ height: '100%' }}>
        <div ref={pedRef}></div>
      </Box>
      {!layout && (
        // Fixed to the viewport so it sits at the centre of the screen regardless of the
        // (collapsed, pre-render) chart-area height — otherwise it lands at the top, under
        // the header / "You are editing" chip. Brand colour to match the rest of the chrome.
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            pointerEvents: 'none',
            zIndex: 1200
          }}
        >
          <CircularProgress size={40} sx={{ color: theme?.header }} />
          <Typography
            variant="body2"
            sx={{ color: theme?.header, fontWeight: 500, letterSpacing: 0.2 }}
          >
            {t(LOADING_PHRASES[loadingStep])}…
          </Typography>
        </Box>
      )}

      {layout?.simplifiedLayout && (
        // Runaway guard (P1.4): a very large pedigree skipped the crossing-minimising search
        // and used the fast descent-forest fallback. Tell the user it's a simplified layout
        // they can refine by hand or with a proxy.
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            bgcolor: '#fff8e1',
            border: '1px solid #ffe082',
            color: '#7a5b00',
            fontSize: 13,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            pointerEvents: 'none',
            zIndex: 1100,
            maxWidth: '80%'
          }}
        >
          {t(
            'Simplified layout for a large pedigree — drag nodes or add a proxy to refine.'
          )}
        </Box>
      )}
    </div>
  );
}
